import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Drawer, Form, Image, Input, InputNumber, Modal, Popconfirm, Segmented, Select, Skeleton, Space, Switch, Tabs, Tag, Tooltip,
  Typography, message as antMessage } from 'antd';
import type { TableColumnsType } from 'antd';
import type { ContentIssue, CreateContentIssueRequest, ProjectDuty, ProjectMember, VideoContentSummary,
  OperationalClaimRecord, StageContractUploadResponse, VideoPackageDefinitionUpdateRequest, VideoPackageDraftRequest,
  VideoPackageOptions, VideoPackageSummary } from '@youkuai/contracts';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Icon } from './icons.tsx';
import { publicationPlanPath } from './publication-return.ts';
import { useAuth } from './auth.tsx';
import { notifyActionFailure } from './action-feedback.ts';
import { addProjectMember, ApiRequestError, endProjectMember, getProject, getProjectAccessProfile, listProjectMemberOptions,
  listProjectMembers, updateProjectMember } from './project-api.ts';
import { resolveProjectWorkspaceAccess } from './project-workspace-access.ts';
import { acceptVideoDelivery, createContentIssue, downloadContentIssueAttachment, downloadVideoResult, draftVideoPackage, getVideoDelivery,
  listContentIssues, listMyContentIssues, listMyVideoPackages, listVideoContentAudit, listVideoContents, listVideoPackages,
  listVideoResults, stageVideoResult, startVideoPackage, submitVideoPackage, returnVideoPackage, confirmVideoPackage,
  previewVideoPackageCompletion, completeVideoPackage, reopenVideoPackage, updateVideoPackageDraft,
  submitVideoResult, updateContentIssue, updateVideoPackageDefinition, videoPackageDefinitionOptions,
  videoPackageOptions, updateContentTitle, updateContentPublicationInfo, videoTaskExecutors } from './video-package-api.ts';
import { addFileMaterial, addLinkMaterial, assignTaskV2, createContents, deleteContentMaterial,
  downloadContentMaterial, getContentCapacity, listContentMaterials, publishMarkdownVersion,
  saveMarkdownDraft, stageTaskMaterial,
  transitionTaskV2, voidContent } from './content-workflow-api.ts';
import { DateInput, shanghaiToday } from './date-input.tsx';
import { contentTitle } from './content-title.ts';
import { changeContentVariant, previewBusinessChange } from './delivery-workflow-api.ts';
import { RequestTimeoutError, withTimeout } from './request-timeout.ts';
import { listOperationalClaims, stageEvidence } from './operations-v2-api.ts';
import type { ModuleStat, ModuleViewMode } from './project-module-table.tsx';
import { ModuleFullDetailDrawer, ModulePageHeader, ModuleTablePanel, ModuleTableView, ModuleViewSwitch } from './project-module-table.tsx';
import { useModuleViewMode } from './project-module-view-mode.ts';

interface FormValues extends Omit<VideoPackageDraftRequest, 'expectedVersion'> {}
interface DefinitionFormValues extends Omit<VideoPackageDefinitionUpdateRequest, 'expectedVersion' | 'capacity'> {}
interface AssignValues { taskId: string; primaryId: string; collaboratorId?: string; primaryShare?: number }
const { Title } = Typography;

const PACKAGE_STATUS_LABEL:Record<VideoPackageSummary['status'],string>={DRAFT:'草稿',SUBMITTED:'待确认',
  RETURNED:'已退回',CONFIRMED:'待启动',ACTIVE:'执行中',COMPLETED:'已完成'};
const PACKAGE_STATUS_STEP:Record<VideoPackageSummary['status'],number>={DRAFT:0,RETURNED:0,SUBMITTED:1,
  CONFIRMED:2,ACTIVE:3,COMPLETED:4};

export function normalizeMoneyInput(value: string): string {
  const normalized = value.trim();
  if (!/^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/.test(normalized)) return normalized;
  const [whole, fraction = ''] = normalized.split('.');
  return `${whole}.${fraction.padEnd(2, '0')}`;
}

export function calculatePackageDefinitionPreview(
  variants: VideoPackageOptions['templates'][number]['variants'],
  counts: Array<{ variantKey: string; count?: number }>, flexibleReserve: string,
): { capacity: number; production: number; publication: number; total: number } {
  const capacity = counts.reduce((sum, row) => sum + (row.count ?? 0), 0);
  const production = variants.reduce((sum, variant) => sum + Number(variant.productionBudget) *
    (counts.find((row) => row.variantKey === variant.variantKey)?.count ?? 0), 0);
  const publication = variants.reduce((sum, variant) => sum + Number(variant.publicationBudget) *
    (counts.find((row) => row.variantKey === variant.variantKey)?.count ?? 0), 0);
  return { capacity, production, publication,
    total: production + publication + Number(flexibleReserve || 0) };
}

export function isPackageDefinitionBelowUsed(
  counts: Array<{ variantKey: string; count?: number }>,
  tiers: Array<{ code: string; used: number }>,
): boolean {
  return counts.some((row) => (row.count ?? 0) < (tiers.find((tier) => tier.code === row.variantKey)?.used ?? 0));
}

export function adjustPackageDefinitionCount(
  counts: Array<{ variantKey: string; count?: number }>, index: number, delta: number,
  minimum = 0, maximum = 100000,
): Array<{ variantKey: string; count: number }> {
  return counts.map((row, rowIndex) => rowIndex === index
    ? { ...row, count: Math.min(maximum, Math.max(minimum, (row.count ?? 0) + delta)) }
    : { ...row, count: row.count ?? 0 });
}

export function normalizePackageDefinitionVariants(
  variants: ReadonlyArray<{ variantKey: string }>,
  counts: ReadonlyArray<{ variantKey?: string; count?: number | null } | undefined>,
): Array<{ variantKey: string; count: number }> {
  const keyedCounts = new Map(counts.flatMap((row) => row?.variantKey
    ? [[row.variantKey, row.count] as const] : []));
  return variants.map((variant, index) => {
    const candidate = keyedCounts.has(variant.variantKey)
      ? keyedCounts.get(variant.variantKey) : counts[index]?.count;
    return { variantKey: variant.variantKey,
      count: typeof candidate === 'number' && Number.isInteger(candidate) && candidate >= 0 ? candidate : 0 };
  });
}

export function canReopenPackage(input: { isAdmin: boolean; owns: boolean }): boolean {
  return input.isAdmin || input.owns;
}

export function packageClaimStatusLabel(claim: Pick<OperationalClaimRecord, 'status' | 'feeStatus'>): string {
  if (claim.status === 'CONFIRMED' && claim.feeStatus === 'PARTIALLY_SETTLED') return '部分结算';
  if (claim.status === 'CONFIRMED' && claim.feeStatus === 'SETTLED') return '已结算';
  return ({ SUBMITTED: '待确认', CONFIRMED: '待结算', RETURNED: '已退回', DIRECT_PAID: '已付款',
    VOID: '已作废', EXTERNALLY_PAID_LEGACY: '历史已付' } as const)[claim.status];
}

export function packageTierLineBudget(tier: Pick<VideoPackageSummary['tiers'][number], 'count' | 'unitPrice'>): string {
  return (Number(tier.unitPrice) * tier.count).toFixed(2);
}

export function buildContentAssignments(taskOptions: Array<{ code: string; name: string }>,
  taskAssignees: Record<string, string | undefined>) {
  return taskOptions.flatMap((task) => {
    const personId = taskAssignees[task.code];
    return personId ? [{ taskKey: task.code,
      assignees: [{ personId, role: 'PRIMARY' as const, shareBps: 10000 }] }] : [];
  });
}

/** 执行人员现在能看到整条内容的完整分工，列表上的“我有 N 项分工”仍需按本人任务计数。 */
export function countMyTasks(tasks: VideoContentSummary['tasks'], personId: string | null): number {
  if (!personId) return 0;
  return tasks.filter((task) => task.executorId === personId ||
    task.assignees.some((row) => row.personId === personId)).length;
}

function failureMessage(error: unknown): string {
  if (error instanceof RequestTimeoutError) return '请求超过 15 秒未响应，请检查网络后重试。';
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      FORBIDDEN: '当前账号没有本项目的对应权限，或关系已撤销。',
      CONFIRMER_INVALID: '启动确认人未在项目中承担“启动确认”职责，或人员状态已失效。',
      MANAGER_INVALID: '服务包负责人未在项目中承担项目或服务包管理职责。',
      STANDARD_NOT_EFFECTIVE: '选择的服务模板在业务日尚未生效，请刷新可用模板。',
      STANDARD_PAIR_MISMATCH: '费率版本必须对应所选模板版本。',
      STANDARD_INCOMPLETE: '标准的档位、任务或费率矩阵不完整，请联系标准维护人。',
      PACKAGE_BUDGET_SHORTFALL: '包预算不足以覆盖固定任务承诺及灵活预留。',
      COMPLETION_BLOCKED: '服务包仍有未完成内容、任务或阻断问题，请先处理完成。',
      VERSION_CONFLICT: '服务包版本已变化，请刷新后再操作。',
      STATE_CONFLICT: '服务包状态已变化，请刷新后再操作。',
      IDEMPOTENCY_PAYLOAD_CONFLICT: '本次操作内容发生变化，请重新发起。',
      TIER_FULL: '该档位名额已满，请刷新内容与名额。',
      SETTLEMENT_LOCKED: '该内容已生成结算单，请先撤销结算单后再操作。',
      TIER_CAPACITY_BELOW_USED: '某项服务的新条数低于已录入条数，请先保持不小于已使用数。',
      TASK_INVALID: '任务与启动时锁定的包标准不匹配。',
      EXECUTOR_INVALID: '执行人的项目关系、账号或收款关系不可用。',
      STAGING_INVALID: '暂存文件过期、已被使用或不属于当前任务，请重新上传。',
      FILE_REJECTED: '文件格式、扩展名或内容不匹配；视频仅接受 MP4，文章接受 PDF、DOCX、TXT、Markdown，均不超过 20 MiB。',
      SCANNER_UNAVAILABLE: '文件扫描暂不可用，请稍后重试。',
      OBJECT_STORAGE_UNAVAILABLE: '私有文件存储暂不可用，未生成成果版本。',
      RESULT_UNAVAILABLE: '成果原始文件不可用，交付没有生效。',
      RESULT_NOT_LATEST: '已有更新成果版本，请刷新后确认最新版本。',
      FEE_SOURCE_MISSING: '必需任务的分派、收款人或锁定费率不完整，不能交付。',
      BUSINESS_DATE_INVALID: '交付业务日不能早于包启动日或晚于今天。',
      CONTENT_ISSUES_BLOCKING: '仍有未处理的阻断问题，处理完成后才能确认最终交付。',
      ATTACHMENT_NOT_READY: '问题截图已过期、已被使用或不属于当前项目，请重新选择上传。',
      MEMBER_INVALID: '该人员账号或执行角色已失效，请刷新候选列表。',
      MEMBER_EXISTS: '该人员已在项目团队中，请在成员列表直接编辑职责，无需重复加入。',
      MEMBER_HAS_ACTIVE_WORK: '该成员仍有未完成任务或待处理问题，请先完成或改派。',
      PLANNER_ROLE_REQUIRED: '该成员还没有「内容策划」全局角色，请先在账号角色中分配后再添加内容策划职责。',
      LEGACY_NO_DUPLICATE: '该业务编号已被本服务包中的其他内容使用，请换一个编号。',
      LEGACY_NO_IMMUTABLE: '新系统内容使用系统内容编号，不能补写到历史业务编号序列。',
      DISPLAY_NO_DUPLICATE: '该内容序号已被本服务包使用，请换一个数字。',
    };
    return messages[error.code] ?? error.message;
  }
  return error instanceof Error ? error.message : '操作失败，请刷新后重试。';
}

const ISSUE_CATEGORY = { PLANNING: '策划', SHOOTING: '拍摄', EDITING: '剪辑', COPY: '文案',
  PUBLISHING: '发布', OTHER: '其他' } as const;

const TASK_TYPE_LABEL: Record<string, string> = {
  PROJECT_MANAGEMENT: '管理', VIDEO_PLANNING: '策划', VIDEO_EDITING: '剪辑', VIDEO_PRODUCTION: '制作',
  ARTICLE_WRITING: '文案', ARTICLE_DESIGN: '设计', PUBLICATION: '发布',
};
export function taskTypeLabel(taskCode: string): string {
  return TASK_TYPE_LABEL[taskCode] ?? '其他';
}

function taskLabel(taskCode: string, tasks?: Array<{ code: string; name: string }>): string {
  const task = tasks?.find((item) => item.code === taskCode);
  return task ? `${task.name}（${task.code}）` : taskCode;
}

// ACCEPTED/IN_PROGRESS 是旧流程留下的历史状态，统一显示为“未完成”。
const TASK_STATUS_LABEL = { UNASSIGNED: '待分派', ASSIGNED: '未完成', ACCEPTED: '未完成',
  IN_PROGRESS: '未完成', SUBMITTED: '待验收', COMPLETED: '已完成', VOID: '已作废' } as const;
const PUBLICATION_TASK_STATUS_LABEL = { UNPLANNED: '待安排', PLANNED: '待发布', IN_PROGRESS: '发布中',
  COMPLETED: '已完成', VOID: '已作废' } as const;

function taskStatusLabel(status: keyof typeof TASK_STATUS_LABEL): string {
  return TASK_STATUS_LABEL[status];
}

export function canHandleContentIssue(input: { canManage: boolean; canResolveIssues: boolean;
  assigneeId: string | null; personId: string | null }): boolean {
  return input.canManage || input.canResolveIssues || (!!input.personId && input.assigneeId === input.personId);
}

/** BUG-0115：图片类附件显示缩略图，点击在当前页浮层放大；PDF 仍然下载查看。 */
function IssueAttachmentPreview({ token, projectId, packageId, contentId, contentType, issueId, attachment }: {
  token: string; projectId: string; packageId: string; contentId: string;
  contentType: PackagePageContentType; issueId: string; attachment: ContentIssue['attachments'][number] }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true; let objectUrl: string | null = null;
    void downloadContentIssueAttachment({ token, projectId, packageId, contentId, contentType,
      issueId, attachmentId: attachment.attachmentId }).then((blob) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob); setUrl(objectUrl);
    }).catch(() => undefined);
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [attachment.attachmentId]);
  if (!url) return <span className="issue-attachment-loading">图片加载中…</span>;
  return <Image src={url} alt={attachment.originalFilename} width={112} height={76}
    className="issue-attachment-thumb" style={{ objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in' }}
    preview={{ mask: '点击放大' }} />;
}

function ContentIssuesPanel({ token, accountId, personId, projectId, packageId, content, contentType,
  canManage, canResolveIssues, executorOptions }: { token: string; accountId: string | null; personId: string | null;
  projectId: string; packageId: string; content: VideoContentSummary; contentType: PackagePageContentType;
  canManage: boolean; canResolveIssues: boolean;
  executorOptions?: Array<{ personId: string; label: string }> | undefined }) {
  const client = useQueryClient();
  type IssueFormValues = Omit<CreateContentIssueRequest, 'expectedVersion' | 'evidenceStagingIds'>;
  const [form] = Form.useForm<IssueFormValues>();
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issueFiles, setIssueFiles] = useState<File[]>([]);
  const [stagedIssueFiles, setStagedIssueFiles] = useState<StageContractUploadResponse[]>([]);
  const defaultIssueAssigneeId = executorOptions?.some((item) => item.personId === personId)
    ? personId : undefined;
  const queryKey = ['content-issues', contentType, accountId, projectId, packageId, content.contentId];
  const issues = useQuery({ queryKey, queryFn: () => listContentIssues({ token, projectId, packageId,
    contentId: content.contentId, contentType }), enabled: !!token && !!accountId });

  async function refresh() {
    await Promise.all([client.invalidateQueries({ queryKey }),
      client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] }),
      client.invalidateQueries({ queryKey: ['my-content-issues', accountId] })]);
  }

  async function create(values: IssueFormValues) {
    setBusyId('create'); setError(null);
    try {
      let staged = stagedIssueFiles.length <= issueFiles.length ? [...stagedIssueFiles] : [];
      for (const file of issueFiles.slice(staged.length)) {
        staged = [...staged, await stageEvidence({ token, projectId,
          key: `issue-evidence-${crypto.randomUUID()}`, file })];
        setStagedIssueFiles(staged);
      }
      await createContentIssue({ token, projectId, packageId, contentId: content.contentId, contentType,
        key: `issue-create-${crypto.randomUUID()}`, issue: { ...values, expectedVersion: content.version,
          blocking: canManage && values.blocking, evidenceStagingIds: staged.map((item) => item.stagingId) } });
      form.resetFields(); setIssueFiles([]); setStagedIssueFiles([]); setCreating(false); await refresh();
    } catch (failure) { setError(failureMessage(failure)); }
    finally { setBusyId(null); }
  }

  async function act(issue: ContentIssue, action: 'assignee' | 'blocking' | 'resolve' | 'reopen' | 'void',
    body: Record<string, unknown>, modal = false) {
    setBusyId(issue.issueId); setError(null);
    try {
      await updateContentIssue({ token, projectId, packageId, contentId: content.contentId, contentType,
        issueId: issue.issueId, action, key: `issue-${action}-${crypto.randomUUID()}`,
        body: { expectedVersion: issue.version, ...body } });
      await refresh();
    } catch (failure) {
      const message = failureMessage(failure); setError(message);
      if (modal) { void antMessage.error(message); throw failure; }
    }
    finally { setBusyId(null); }
  }

  async function viewAttachment(issue: ContentIssue, attachment: ContentIssue['attachments'][number]) {
    try {
      const blob = await downloadContentIssueAttachment({ token, projectId, packageId, contentId: content.contentId,
        contentType, issueId: issue.issueId, attachmentId: attachment.attachmentId });
      const url = URL.createObjectURL(blob); const anchor = document.createElement('a');
      anchor.href = url; anchor.target = '_blank'; anchor.rel = 'noopener'; anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (failure) { const message = failureMessage(failure); setError(message); void antMessage.error(message); }
  }

  return <div className="content-issues-panel">
    <div className="content-issues-heading"><div><strong>问题与修改</strong>
      <span>独立于分工任务；普通问题提醒，阻断问题会拦截最终交付。</span></div>
      {!['DELIVERED', 'VOID'].includes(content.status) && <Button type="primary" onClick={() => setCreating((value) => !value)}>
        {creating ? '收起' : '提出问题'}</Button>}</div>
    {creating && <Form form={form} layout="vertical" className="issue-create-form" onFinish={(values) => void create(values)}
      initialValues={{ category: 'OTHER', blocking: false, assigneeId: defaultIssueAssigneeId }}>
      <div className="issue-form-grid"><Form.Item name="category" label="问题类型" rules={[{ required: true }]}>
        <Select options={Object.entries(ISSUE_CATEGORY).map(([value, label]) => ({ value, label }))} /></Form.Item>
        <Form.Item name="location" label="出现位置"><Input maxLength={160} placeholder="如 00:42 / 第 3 段" /></Form.Item></div>
      <Form.Item name="title" label="问题标题" rules={[{ required: true }]}><Input maxLength={160} placeholder="一句话说明需要修复什么" /></Form.Item>
      <Form.Item name="description" label="处理要求"><Input.TextArea maxLength={2000} rows={3} placeholder="补充复现位置、修改标准或参考信息" /></Form.Item>
      <label className="issue-evidence-field"><span>问题截图（可选，最多 5 张）</span>
        <input type="file" accept="image/png,image/jpeg" multiple onChange={(event) => {
          const files = Array.from(event.target.files ?? []).slice(0, 5);
          setIssueFiles(files); setStagedIssueFiles([]); event.target.value = '';
        }} />
        {issueFiles.length > 0 && <small>{issueFiles.map((file) => file.name).join('、')}</small>}</label>
      <div className="issue-form-grid"><Form.Item name="assigneeId" label="处理人">
        <Select allowClear disabled={!canManage} options={executorOptions?.map((item) => ({ value: item.personId, label: item.label })) ?? []} /></Form.Item>
        {canManage && <Form.Item name="blocking" label="阻断最终交付" valuePropName="checked"><Switch /></Form.Item>}</div>
      <Button htmlType="submit" type="primary" loading={busyId === 'create'}>提交问题</Button>
    </Form>}
    {error && <Alert type="error" showIcon title={error} />}
    {issues.isPending && <Skeleton active paragraph={{ rows: 3 }} />}
    {issues.isError && <Alert type="warning" title="问题列表读取失败" description={failureMessage(issues.error)} />}
    {issues.data?.items.length === 0 && <div className="issue-empty">暂无问题。成果仍需按最终验收标准确认。</div>}
    <div className="issue-list">{issues.data?.items.map((issue) => {
      const canHandle = canHandleContentIssue({ canManage, canResolveIssues,
        assigneeId: issue.assigneeId, personId });
      return <article key={issue.issueId} className={`issue-card issue-${issue.status.toLowerCase()} ${issue.blocking ? 'is-blocking' : ''}`}>
        <header><div><Tag color={issue.blocking ? 'red' : issue.status === 'RESOLVED' ? 'green' : 'gold'}>
          {issue.blocking ? '阻断' : issue.status === 'RESOLVED' ? '已处理' : issue.status === 'VOID' ? '已作废' : '待处理'}</Tag>
          <Tag>{ISSUE_CATEGORY[issue.category]}</Tag>{issue.location && <span>{issue.location}</span>}</div><small>V{issue.version}</small></header>
        <h4>{issue.title}</h4>{issue.description && <p>{issue.description}</p>}
        {issue.attachments.length > 0 && <div className="issue-attachments">{issue.attachments.map((attachment) =>
          attachment.detectedMime === 'application/pdf'
            ? <Button key={attachment.attachmentId} size="small" onClick={() => void viewAttachment(issue, attachment)}>
              📎 {attachment.originalFilename}</Button>
            : <IssueAttachmentPreview key={attachment.attachmentId} token={token} projectId={projectId}
              packageId={packageId} contentId={content.contentId} contentType={contentType}
              issueId={issue.issueId} attachment={attachment} />)}</div>}
        <footer><span>处理人：{executorOptions?.find((item) => item.personId === issue.assigneeId)?.label ??
          (issue.assigneeId === personId ? '我' : issue.assigneeId ? '已分派' : '待分派')}</span><Space wrap>
          {canManage && issue.status !== 'VOID' && <Select size="small" allowClear placeholder="改派" value={issue.assigneeId}
            style={{ width: 120 }} options={executorOptions?.map((item) => ({ value: item.personId, label: item.label })) ?? []}
            onChange={(assigneeId) => void act(issue, 'assignee', { assigneeId: assigneeId ?? null })} />}
          {canManage && issue.status !== 'VOID' && <Button size="small" danger={issue.blocking}
            onClick={() => void act(issue, 'blocking', { blocking: !issue.blocking })}>{issue.blocking ? '取消阻断' : '设为阻断'}</Button>}
          {canHandle && issue.status === 'OPEN' && <Button size="small" type="primary" loading={busyId === issue.issueId}
            onClick={() => void act(issue, 'resolve', {})}>标记已处理</Button>}
          {canHandle && issue.status === 'RESOLVED' && <Button size="small" loading={busyId === issue.issueId}
            onClick={() => void act(issue, 'reopen', {})}>重新打开</Button>}
          {canManage && issue.status !== 'VOID' && <Button size="small" danger onClick={() => {
            let reason = ''; Modal.confirm({ title: '作废这条问题？', content: <Input placeholder="填写作废原因"
              onChange={(event) => { reason = event.target.value; }} />, okText: '确认作废', okButtonProps: { danger: true },
              onOk: () => reason.trim() ? act(issue, 'void', { reason: reason.trim() }, true) :
                (void antMessage.error('请填写作废原因'), Promise.reject(new Error('请填写作废原因'))) });
          }}>作废</Button>}
        </Space></footer>
      </article>;
    })}</div>
  </div>;
}

function VideoAuditPanel({ token, accountId, projectId, packageId, contentId, contentType }: {
  token: string; accountId: string | null; projectId: string; packageId: string; contentId: string;
  contentType: PackagePageContentType }) {
  const [open, setOpen] = useState(false);
  const audit = useQuery({ queryKey: ['video-audit', contentType, accountId, projectId, packageId, contentId],
    queryFn: () => listVideoContentAudit(token, projectId, packageId, contentId, contentType),
    enabled: open && !!token && !!accountId });
  return <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
    <summary>查看操作与审计记录</summary>
    {audit.isPending && <span>读取中…</span>}
    {audit.isError && <Alert type="warning" title="审计读取失败" description={failureMessage(audit.error)} />}
    {audit.data?.items.map((item) => <div key={item.eventId} className="package-facts">
      <span>{item.action}</span><span>{item.occurredAt.replace('T', ' ').slice(0, 19)}</span>
      <span>操作者 {item.actorId ?? '系统'}</span></div>)}
  </details>;
}

function ContentTaskRow({ token, accountId, personId, projectId, packageId, content, task, contentType,
  canManage, canAssignTasks, canProgressTasks, canProgressOthers, canReturnSubmittedTasks,
  executorOptions }: { token: string; accountId: string | null; personId: string | null; projectId: string;
  packageId: string; content: VideoContentSummary; task: VideoContentSummary['tasks'][number];
  contentType: PackagePageContentType; canManage: boolean; canAssignTasks: boolean; canProgressTasks: boolean;
  canProgressOthers?: boolean | undefined; canReturnSubmittedTasks: boolean;
  executorOptions?: Array<{ personId: string; label: string }> | undefined }) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMine = task.assignees.some((row) => row.personId === personId) || task.executorId === personId;
  const action = taskProgressAction(task.status, content.status, isMine, canManage || canProgressOthers === true,
    canProgressTasks || canProgressOthers === true);
  const canReturnForRework = canReturnSubmittedTask(task.status, content.status, canReturnSubmittedTasks);
  const canReassign = canReassignTask(canAssignTasks, task.status);
  const primaryAssignee = task.assignees.find((row) => row.role === 'PRIMARY');
  const currentAssigneeId = primaryAssignee?.personId ?? task.executorId ?? undefined;
  const assigneeOptions = (executorOptions ?? []).map((row) => ({ value: row.personId, label: row.label }));
  if (currentAssigneeId && !assigneeOptions.some((row) => row.value === currentAssigneeId)) {
    assigneeOptions.push({ value: currentAssigneeId,
      label: primaryAssignee?.displayName ?? task.executorName ?? '当前负责人' });
  }

  async function refresh() {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] }),
      client.invalidateQueries({ queryKey: ['video-audit', contentType, accountId, projectId, packageId, content.contentId] }),
    ]);
  }

  async function toggle(checked: boolean) {
    const nextAction = checked ? 'complete' : 'reopen';
    if (nextAction !== action) return;
    setBusy(true); setError(null);
    try {
      await transitionTaskV2({ token, projectId, taskId: task.taskId, action: nextAction,
        change: { expectedVersion: task.version }, key: `task-${nextAction}-${crypto.randomUUID()}` });
      await refresh();
    } catch (failure) { setError(failureMessage(failure)); }
    finally { setBusy(false); }
  }

  async function reassign(nextPersonId: string) {
    setAssignBusy(true); setError(null);
    try {
      await withTimeout((signal) => assignTaskV2({ token, projectId, taskId: task.taskId, signal,
        key: `task-assign-${crypto.randomUUID()}`, assignment: { expectedVersion: task.version,
          assignees: [{ personId: nextPersonId, role: 'PRIMARY', shareBps: 10000 }] } }));
      setAssignBusy(false);
      void refresh();
    } catch (failure) { setAssignBusy(false); setError(failureMessage(failure)); }
  }

  function returnForRework() {
    let reason = '';
    Modal.confirm({ title: '退回这项成果重做？', okText: '确认退回', okButtonProps: { danger: true },
      content: <div><p>任务和整条内容会回到制作中，执行人可重新提交成果；锁定金额不会改变。</p>
        <Input.TextArea rows={3} placeholder="填写退回原因（至少 2 个字）"
          onChange={(event) => { reason = event.target.value; }} /></div>,
      onOk: async () => {
        const normalized = reason.trim();
        if (normalized.length < 2) {
          void antMessage.error('请填写至少 2 个字的退回原因');
          throw new Error('请填写至少 2 个字的退回原因');
        }
        setBusy(true); setError(null);
        try {
          await transitionTaskV2({ token, projectId, taskId: task.taskId, action: 'return',
            change: { expectedVersion: task.version, reason: normalized },
            key: `task-return-${crypto.randomUUID()}` });
          await refresh();
        } catch (failure) { setError(failureMessage(failure)); throw failure; }
        finally { setBusy(false); }
      } });
  }

  return <div className={`content-subtask-row ${isMine ? 'is-mine' : ''}`}>
    <div className="content-subtask-copy"><strong title={task.taskCode}><Tag className="task-type-tag">{taskTypeLabel(task.taskCode)}</Tag>{' '}
      {task.taskName || task.taskCode}</strong><small>{task.assignees.length ?
      task.assignees.map((row) => `${row.displayName}${row.role === 'PRIMARY' ? '（主责）' : ''}`).join(' / ') : '待分派'}
      {isMine ? ' · 我的分工' : ''}</small>
      {task.returnReason && task.status === 'IN_PROGRESS' && <small className="task-return-reason">
        退回原因：{task.returnReason}{task.returnedByName ? ` · ${task.returnedByName}` : ''}
      </small>}</div>
    {canReassign && <Select size="small" className="subtask-assignee-select" showSearch optionFilterProp="label"
      placeholder="选择负责人" loading={assignBusy} disabled={assignBusy}
      value={currentAssigneeId} onChange={(value: string | undefined) => value && void reassign(value)}
      options={assigneeOptions} />}
    {shouldShowTaskStatusTag(action) && <Tag color={task.status === 'COMPLETED' ? 'green' : task.status === 'ASSIGNED' ? 'blue' :
      task.status === 'VOID' ? 'default' : task.status === 'UNASSIGNED' ? 'gold' : 'blue'}>
      {taskStatusLabel(task.status)}</Tag>}
    {action && <Switch size="small" checked={task.status === 'COMPLETED'} loading={busy}
      onChange={(checked) => void toggle(checked)} checkedChildren="完成" unCheckedChildren="未完成" />}
    {canReturnForRework && <Button size="small" danger loading={busy} onClick={returnForRework}>退回重做</Button>}
    {error && <Alert className="subtask-inline-error" type="error" showIcon title={error} />}
  </div>;
}

export function shouldShowTaskStatusTag(action: 'complete' | 'reopen' | null): boolean {
  return action === null;
}

/** BUG-0106/0114：未交付内容可完成或撤销完成；交付或作废后锁定。 */
export function taskProgressAction(status: string, contentStatus: string, isMine: boolean,
  canManage: boolean, canProgressTasks: boolean): 'complete' | 'reopen' | null {
  if (!canProgressTasks || (!isMine && !canManage) || ['DELIVERED', 'VOID'].includes(contentStatus)) return null;
  if (['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS'].includes(status)) return 'complete';
  return status === 'COMPLETED' ? 'reopen' : null;
}

/** BUG-0181：只有明确承担验收职责的人，才能把待验收成果退回制作。 */
export function canReturnSubmittedTask(status: string, contentStatus: string, canReturn: boolean): boolean {
  return canReturn && status === 'SUBMITTED' && contentStatus === 'PENDING_DELIVERY';
}

/**
 * 未交付前，有改派权限的角色可以调整任何未作废任务；已完成任务改派后仍保持“完成”（BUG-0080）。
 * 已交付内容由服务端彻底锁定，这里不重复判断。
 */
export function canReassignTask(canManage: boolean, status: string): boolean {
  // BUG-0105：已交付内容不允许改派，需先撤销交付；作废任务同样不可改派。
  return canManage && !['VOID', 'DELIVERED'].includes(status);
}

function ContentResultPanel({ token, accountId, personId, projectId, packageId, content, contentType,
  canManage }: { token: string; accountId: string | null; personId: string | null; projectId: string;
  packageId: string; content: VideoContentSummary; contentType: PackagePageContentType; canManage: boolean }) {
  const client = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [deliveryOn, setDeliveryOn] = useState(() => new Intl.DateTimeFormat('sv-SE',
    { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()));
  const [deliveryChannel, setDeliveryChannel] = useState('客户确认');
  const uploadAttempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const submissionAttempt = useRef<{ stagingId: string; key: string } | null>(null);
  const deliveryAttempt = useRef<{ payload: string; key: string } | null>(null);
  const resultTasks = content.tasks.filter((task) => !['UNASSIGNED', 'VOID'].includes(task.status));
  const resultQueries = useQueries({ queries: resultTasks.map((task) => ({
    queryKey: ['video-results', accountId, projectId, packageId, content.contentId, task.taskId],
    queryFn: () => listVideoResults({ token, projectId, packageId, contentType,
      contentId: content.contentId, taskId: task.taskId }), enabled: !!token && !!accountId,
  })) });
  const results = resultQueries.flatMap((query, index) => (query.data?.items ?? []).map((result) => ({
    result, task: resultTasks[index]!,
  }))).sort((left, right) => right.result.submittedAt.localeCompare(left.result.submittedAt));
  const latest = results[0];
  const uploadTask = content.tasks.find((task) => task.assignees.some((row) => row.personId === personId) &&
    task.status === 'IN_PROGRESS') ?? content.tasks.find((task) => task.assignees.some((row) => row.personId === personId)
      && task.status === 'COMPLETED');
  const delivery = useQuery({ queryKey: ['video-delivery', accountId, projectId, packageId, content.contentId],
    queryFn: () => getVideoDelivery({ token, projectId, packageId, contentId: content.contentId, contentType }),
    enabled: !!token && !!accountId && canManage && results.length > 0 });
  const feeCents = content.tasks.reduce((sum, row) => {
    const amount = row.lockedAmount;
    return amount && /^\d+\.\d{2}$/.test(amount) ? sum + BigInt(amount.replace('.', '')) : sum;
  }, 0n);
  const estimatedAmount = `${feeCents / 100n}.${String(feeCents % 100n).padStart(2, '0')}`;

  async function submit() {
    if (!file || !uploadTask || busy) return;
    const validVideo = contentType === 'VIDEO' && file.size >= 12 && /\.mp4$/i.test(file.name);
    const validArticle = contentType === 'ARTICLE' && /\.(?:pdf|docx|txt|md)$/i.test(file.name);
    if (file.size < 1 || file.size > 20_971_520 || (!validVideo && !validArticle)) {
      setError(contentType === 'VIDEO' ? '请选择不超过 20 MiB 的 MP4 文件。' :
        '请选择不超过 20 MiB 的 PDF、DOCX、TXT 或 Markdown 稿件。'); return;
    }
    const fingerprint = `${uploadTask.taskId}:${uploadTask.version}:${file.name}:${file.size}:${file.lastModified}`;
    if (uploadAttempt.current?.fingerprint !== fingerprint) {
      uploadAttempt.current = { fingerprint, key: `video-upload-${crypto.randomUUID()}` };
    }
    setBusy(true); setError(null); setNotice(null);
    try {
      const staged = await stageVideoResult({ token, projectId, packageId, contentId: content.contentId, contentType,
        taskId: uploadTask.taskId, expectedVersion: uploadTask.version, key: uploadAttempt.current.key, file });
      if (submissionAttempt.current?.stagingId !== staged.stagingId) {
        submissionAttempt.current = { stagingId: staged.stagingId, key: `video-submit-${crypto.randomUUID()}` };
      }
      const saved = await submitVideoResult({ token, projectId, packageId, contentId: content.contentId, contentType,
        taskId: uploadTask.taskId, expectedVersion: uploadTask.version, stagingId: staged.stagingId,
        key: submissionAttempt.current.key });
      setNotice(`内容成果 V${saved.versionNo} 已提交审核。子任务会在整条内容交付后统一完成。`);
      setFile(null); uploadAttempt.current = null; submissionAttempt.current = null;
      await Promise.all([client.invalidateQueries({ queryKey: ['video-results', accountId, projectId, packageId, content.contentId] }),
        client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] }),
        client.invalidateQueries({ queryKey: ['video-audit', contentType, accountId, projectId, packageId, content.contentId] })]);
    } catch (failure) { setError(failureMessage(failure)); }
    finally { setBusy(false); }
  }

  async function download(taskId: string, resultId: string, originalFilename: string) {
    setError(null);
    try {
      const blob = await downloadVideoResult({ token, projectId, packageId, contentId: content.contentId, contentType,
        taskId, resultId });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a'); anchor.href = url;
      anchor.download = originalFilename || `${contentType.toLowerCase()}-result-${resultId}`; anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (failure) { setError(failureMessage(failure)); }
  }

  function confirm(resultId: string) {
    if (content.issueStats.blocking > 0 || !deliveryOn || !deliveryChannel.trim() ||
      !content.tasks.every((row) => !['UNASSIGNED', 'VOID'].includes(row.status))) return;
    const payload = JSON.stringify({ projectId, packageId, contentId: content.contentId, contentVersion: content.version,
      resultId, businessOn: deliveryOn, channel: deliveryChannel.trim() });
    if (deliveryAttempt.current?.payload !== payload) {
      deliveryAttempt.current = { payload, key: `delivery-${crypto.randomUUID()}` };
    }
    Modal.confirm({ title: `确认整条${contentType === 'VIDEO' ? '视频' : '文章'}交付？`,
      content: `交付后 ${content.tasks.length} 项分工任务统一完成，并按锁定费率生成应计 ¥${estimatedAmount}。交付日 ${deliveryOn}，渠道 ${deliveryChannel}。`,
      okText: '确认接受交付', onOk: async () => {
        setError(null);
        try {
          const accepted = await acceptVideoDelivery({ token, projectId, packageId, contentId: content.contentId, contentType,
            expectedVersion: content.version, resultId, businessOn: deliveryOn,
            channel: deliveryChannel, key: deliveryAttempt.current!.key });
          setNotice(`已确认交付，${accepted.taskCount} 项任务完成；已自动生成 ${accepted.accrualRows} 条应计，合计 ¥${accepted.accruedAmount}，待财务结算。`);
          deliveryAttempt.current = null;
          await Promise.all([client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] }),
            client.invalidateQueries({ queryKey: ['video-delivery', accountId, projectId, packageId, content.contentId] }),
            client.invalidateQueries({ queryKey: ['video-audit', contentType, accountId, projectId, packageId, content.contentId] })]);
        } catch (failure) {
          const message = failureMessage(failure); setError(message); void antMessage.error(message); throw failure;
        }
      } });
  }

  return <div className="content-result-panel">
    {resultQueries.some((query) => query.isError) && <Alert type="warning" title="成果版本读取失败"
      description={failureMessage(resultQueries.find((query) => query.isError)?.error)} />}
    {resultQueries.some((query) => query.isPending) && <span className="result-loading">读取内容成果…</span>}
    {!resultQueries.some((query) => query.isPending) && results.length === 0 && <div className="result-empty">
      <strong>尚未提交整条内容</strong><span>子任务只推进制作进度，不需单独上传或验收。</span></div>}
    {latest && <article className="result-latest"><div className="result-file-mark">{contentType === 'VIDEO' ? 'MP4' : 'DOC'}</div>
      <div><small>LATEST · 内容成果 V{latest.result.versionNo}</small><strong>{latest.result.originalFilename}</strong>
        <span>{latest.result.submittedAt.replace('T', ' ').slice(0, 16)} · {latest.task.executorName ?? '执行人'}提交</span></div>
      <Button onClick={() => void download(latest.task.taskId, latest.result.resultId, latest.result.originalFilename)}>鉴权下载</Button></article>}
    {canManage && content.issueStats.blocking > 0 && content.status === 'PENDING_DELIVERY' &&
      <Alert type="error" showIcon title={`有 ${content.issueStats.blocking} 个阻断问题未处理，暂不能确认最终交付`} />}
    {canManage && latest && content.status === 'PENDING_DELIVERY' && <div className="delivery-confirm-bar">
      <div><DateInput aria-label="交付业务日" value={deliveryOn} onChange={(event) => setDeliveryOn(event.target.value)} />
        <Input aria-label="交付渠道" maxLength={120} value={deliveryChannel}
          onChange={(event) => setDeliveryChannel(event.target.value)} /></div>
      <Button type="primary" disabled={content.issueStats.blocking > 0 || content.tasks.some((task) => task.status === 'UNASSIGNED')}
        onClick={() => confirm(latest.result.resultId)}>确认整条交付</Button></div>}
    {delivery.data?.delivery && <Tag color="green">已交付 · {delivery.data.delivery.businessOn} · 应计 ¥{delivery.data.delivery.accruedAmount}（{delivery.data.delivery.accrualRows} 条）</Tag>}
    {uploadTask && !['DELIVERED', 'VOID'].includes(content.status) && <div className="content-submit-box"><div>
      <strong>{latest ? '提交新版本' : `提交整条${contentType === 'VIDEO' ? '视频' : '文章'}审核`}</strong>
      <span>上传后整条内容进入待审核，不会单独完成某个子任务。</span></div>
      <input type="file" accept={contentType === 'VIDEO' ? 'video/mp4,.mp4' :
        'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,.pdf,.docx,.txt,.md'}
        aria-label={contentType === 'VIDEO' ? '视频成果' : '文章稿件'}
        onChange={(event) => { setFile(event.target.files?.[0] ?? null); uploadAttempt.current = null;
          submissionAttempt.current = null; setError(null); }} />
      <Button type="primary" disabled={!file} loading={busy} onClick={() => void submit()}>上传并提交审核</Button>
    </div>}
    {results.length > 1 && <details className="result-history"><summary>查看历史版本（{results.length - 1}）</summary>
      {results.slice(1).map(({ result, task }) => <div key={result.resultId}><span>V{result.versionNo} · {result.originalFilename}</span>
        <Button size="small" onClick={() => void download(task.taskId, result.resultId, result.originalFilename)}>下载</Button></div>)}</details>}
    {error && <Alert type="error" title={error} />}
    {notice && <Alert type="success" title={notice} />}
  </div>;
}

function contentStatusLabel(status: string): string {
  if (status === 'PLACEHOLDER' || status === 'PLANNED' || status === 'TIERED') return '待分派';
  if (status === 'PENDING_DELIVERY') return '待登记';
  if (status === 'DELIVERED') return '已交付';
  return '制作中';
}

/**
 * BUG-0151：粘贴发布标签时按 #、空格、换行和常见分隔符拆分，并自动去重。
 * 单个词（不含任何分隔符）返回空数组，交给输入框按普通标签录入处理。
 */
export function parsePublicationTags(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed || !/[#\s,，、;；]/.test(trimmed)) return [];
  return [...new Set(trimmed.split(/[#\s,，、;；]+/).map((token) => token.trim()).filter(Boolean))];
}

/**
 * BUG-0142：内容策划在内容详情里预先填写发布标题、发布正文和发布标签，
 * 新建发布计划时自动带出。项目负责人同样可以编辑。
 */
export function ContentPublicationInfoPanel({ token, accountId, projectId, packageId, contentType, content,
  canEdit }: { token: string; accountId: string | null; projectId: string; packageId: string;
  contentType: PackagePageContentType; content: VideoContentSummary; canEdit: boolean }) {
  const client = useQueryClient();
  const info = content.publicationInfo;
  const [title, setTitle] = useState(info.title ?? content.title ?? '');
  const [copyDraft, setCopyDraft] = useState(info.copy ?? '');
  const [tags, setTags] = useState<string[]>(info.tags);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => { setTitle(info.title ?? content.title ?? ''); setCopyDraft(info.copy ?? '');
    setTags(info.tags); setError(null); }, [content.contentId, info.copy, info.tags, info.title, content.title]);

  async function save() {
    if (busy) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await updateContentPublicationInfo({ token, projectId, contentType, packageId, contentId: content.contentId,
        key: `content-publication-info-${crypto.randomUUID()}`, expectedVersion: content.version,
        publicationTitle: title.trim() || null, publicationCopy: copyDraft, publicationTags: tags });
      setNotice('发布信息已保存，新建发布计划时会自动带出。');
      await Promise.all([
        client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] }),
        client.invalidateQueries({ queryKey: ['publication-workspace', projectId] }),
        client.invalidateQueries({ queryKey: ['video-content', contentType, accountId, projectId, packageId, content.contentId] }),
      ]);
    } catch (failure) { setError(failureMessage(failure)); } finally { setBusy(false); }
  }

  const filled = !!info.title || !!info.copy || info.tags.length > 0;
  if (!canEdit && !filled) return null;
  return <section className="content-publication-info">
    <header><div><h3>发布信息</h3><p>策划先填写发布标题、正文与标签，新建发布计划时会自动带出。</p></div>
      {canEdit && <Button size="small" type="primary" loading={busy} onClick={() => void save()}>保存发布信息</Button>}
    </header>
    {error && <Alert type="error" showIcon title={error} closable onClose={() => setError(null)} />}
    {notice && <Alert type="success" showIcon title={notice} closable onClose={() => setNotice(null)} />}
    {canEdit ? <div className="content-publication-info-form">
      <label><span>发布标题</span><Input maxLength={200} value={title} placeholder="默认可直接用内容标题"
        onChange={(event) => setTitle(event.target.value)} /></label>
      <label><span>发布正文</span><Input.TextArea rows={3} maxLength={5000} value={copyDraft}
        placeholder="粘贴该内容在各平台发布的正文" onChange={(event) => setCopyDraft(event.target.value)} /></label>
      <label><span>发布标签</span>
        <div className="publication-tags-field" onPaste={(event) => {
          const parsed = parsePublicationTags(event.clipboardData.getData('text'));
          if (!parsed.length) return;
          event.preventDefault();
          setTags((current) => [...new Set([...current, ...parsed])].slice(0, 30));
        }}>
          <Select mode="tags" value={tags} placeholder="输入标签后回车；粘贴 #标签 会自动拆分"
            tokenSeparators={[',', '，']} onChange={(value: string[]) => setTags(value.slice(0, 30))}
            options={[]} />
        </div></label>
    </div> : <dl className="content-publication-info-read">
      <div><dt>发布标题</dt><dd>{info.title ?? content.title ?? '未填写'}</dd></div>
      <div><dt>发布正文</dt><dd>{info.copy ?? '未填写'}</dd></div>
      <div><dt>发布标签</dt><dd>{info.tags.length ? info.tags.join(' / ') : '未填写'}</dd></div>
    </dl>}
  </section>;
}

export function ContentAssignmentModal({ token, accountId, projectId, packageId, content, contentType,
  packageTiers, executorOptions, taskOptions, onClose, onAssigned }: { token: string; accountId: string | null;
  projectId: string; packageId: string; content: VideoContentSummary; contentType: PackagePageContentType;
  packageTiers: Array<{ code: string; count: number; used: number }>;
  executorOptions?: Array<{ personId: string; label: string }> | undefined;
  taskOptions?: Array<{ code: string; name: string }> | undefined;
  onClose: () => void; onAssigned?: ((content: VideoContentSummary) => void) | undefined }) {
  const client = useQueryClient();
  const [form] = Form.useForm<AssignValues>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const availableTasks = content.tasks.filter((task) => !['COMPLETED', 'VOID'].includes(task.status));
  const primaryShare = Form.useWatch('primaryShare', form) ?? 100;
  const collaboratorId = Form.useWatch('collaboratorId', form);

  async function assign(values: AssignValues) {
    const task = content.tasks.find((row) => row.taskId === values.taskId);
    if (!task || busy) return;
    setBusy(true); setError(null);
    try {
      const primaryBps = collaboratorId ? Math.round((values.primaryShare ?? 100) * 100) : 10000;
      await withTimeout((signal) => assignTaskV2({ token, projectId, taskId: task.taskId, signal,
        key: `content-assignment-${crypto.randomUUID()}`, assignment: { expectedVersion: task.version,
          assignees: [{ personId: values.primaryId, role: 'PRIMARY', shareBps: primaryBps },
            ...(values.collaboratorId ? [{ personId: values.collaboratorId, role: 'COLLABORATOR' as const,
              shareBps: 10000 - primaryBps }] : [])] } }));
      setBusy(false);
      void Promise.all([
        client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] }),
        client.invalidateQueries({ queryKey: ['video-packages', contentType, accountId, projectId] }),
      ]);
      onAssigned?.(content); onClose();
    } catch (failure) { setBusy(false); setError(failureMessage(failure)); }
  }

  const unassigned = content.tasks.filter((task) => task.status === 'UNASSIGNED').length;
  return <Modal open title="配置内容任务分工"
    okText="保存这项分工" cancelText="取消" confirmLoading={busy}
    onCancel={onClose} onOk={() => form.submit()} destroyOnHidden>
    <div className="content-assignment-intro"><strong>{contentTitle(content.displayNo, content.legacyNo, content.title ?? content.contentCode)}</strong>
      <span>当前规格 {content.tierCode}，还有 {unassigned} 项职责待明确。简单任务只选主责；复杂任务可增加一名协作人并分配比例。</span></div>
    <Form form={form} layout="vertical" onFinish={(values) => void assign(values)}
      initialValues={{ taskId: availableTasks.find((task) => task.status === 'UNASSIGNED')?.taskId ?? availableTasks[0]?.taskId,
        primaryShare: 100 }}>
      <Form.Item name="taskId" label="制作任务" rules={[{ required: true, message: '请选择制作任务' }]}>
        <Select options={availableTasks.map((task) => ({ value: task.taskId,
          label: `${task.taskName || taskLabel(task.taskCode, taskOptions)} · ${taskStatusLabel(task.status)}` }))} /></Form.Item>
      <Form.Item name="primaryId" label="主要负责人" rules={[{ required: true, message: '请选择主要负责人' }]}>
        <Select showSearch optionFilterProp="label" options={executorOptions?.map((item) => ({
          value: item.personId, label: item.label })) ?? []} /></Form.Item>
      <Form.Item name="collaboratorId" label="协作人（可选）">
        <Select allowClear showSearch optionFilterProp="label" options={executorOptions?.map((item) => ({
          value: item.personId, label: item.label })) ?? []} /></Form.Item>
      {collaboratorId && <Form.Item name="primaryShare" label={`主责比例（协作人 ${100 - primaryShare}%）`}
        rules={[{ required: true }, { type: 'number', min: 1, max: 99 }]}><InputNumber min={1} max={99} precision={0} addonAfter="%" /></Form.Item>}
    </Form>
    {error && <Alert type="error" showIcon title={error} />}
  </Modal>;
}

export type ContentNextStepAction = 'ASSIGN' | 'REVIEW' | 'WAITING_REVIEW' | 'VIEW_RESULT' | 'VIEW_DELIVERED';

export function describeContentNextStep({ status, canManage, canAssignTasks, hasTier, unassigned }: {
  status: string; canManage: boolean; canAssignTasks: boolean; hasTier: boolean; unassigned: number;
}): { title: string; description: string; action: ContentNextStepAction } {
  if (status === 'DELIVERED') return {
    title: '这条内容已完成交付', description: '成果、分工与应计已统一归档。', action: 'VIEW_DELIVERED',
  };
  if (status === 'PENDING_DELIVERY' && canAssignTasks && unassigned > 0) return {
    title: `交付登记前还需补齐 ${unassigned} 项负责人`,
    description: '不需要子任务分别提交；只需明确责任后标记完成，再前往交付登记。', action: 'ASSIGN',
  };
  if (status === 'PENDING_DELIVERY' && canManage) return {
    title: '全部分工已完成，可前往交付登记', description: '登记真实交付日期与成功证据后，整条内容与分工将统一标记为已交付。', action: 'REVIEW',
  };
  if (status === 'PENDING_DELIVERY') return unassigned > 0 ? {
    title: `还有 ${unassigned} 项制作职责待项目负责人或内容策划补齐`,
    description: '补齐后项目负责人才能登记交付；你可以在下方查看完整分工。', action: 'VIEW_RESULT',
  } : {
    title: '整条内容分工已全部完成', description: '等待项目负责人完成交付登记后，这条内容会标记为已交付。', action: 'WAITING_REVIEW',
  };
  if (!hasTier) return canAssignTasks ? {
    title: '从内容本身开始：设置标题、档位和第一项分工',
    description: '服务包只提供容量和标准，不会离开当前内容。', action: 'ASSIGN',
  } : {
    title: '内容尚未选档', description: '等待项目负责人或内容策划选择档位并生成制作任务。', action: 'VIEW_RESULT',
  };
  if (unassigned > 0) return canAssignTasks ? {
    title: `还有 ${unassigned} 项制作职责待明确`, description: '在当前内容中补齐负责人，再按实际进度推进。', action: 'ASSIGN',
  } : {
    title: `还有 ${unassigned} 项制作职责待项目负责人或内容策划补齐`,
    description: '分工由项目负责人或内容策划维护；你可以在下方查看完整分工。', action: 'VIEW_RESULT',
  };
  return {
    title: '分工已明确，按进度标记完成', description: '全部分工标记完成后，内容会自动进入交付登记。', action: 'VIEW_RESULT',
  };
}

function MarkdownPreview({ value }: { value: string }) {
  const lines = value.split('\n');
  return <div className="markdown-live-preview">{lines.map((line, index) => {
    if (line.startsWith('### ')) return <h4 key={index}>{line.slice(4)}</h4>;
    if (line.startsWith('## ')) return <h3 key={index}>{line.slice(3)}</h3>;
    if (line.startsWith('# ')) return <h2 key={index}>{line.slice(2)}</h2>;
    if (line.startsWith('- ')) return <div className="markdown-list-line" key={index}>• {line.slice(2)}</div>;
    return line.trim() ? <p key={index}>{line}</p> : <br key={index} />;
  })}</div>;
}

function TaskMaterialsPanel({ token, accountId, personId, projectId, packageId, content, contentType, canManage }: {
  token: string; accountId: string | null; personId: string | null; projectId: string; packageId: string;
  content: VideoContentSummary; contentType: PackagePageContentType; canManage: boolean }) {
  const client = useQueryClient();
  // BUG-0188：内容材料始终按内容读取；任务仅用于新材料的来源标注。
  const isOwnTask = (task: VideoContentSummary['tasks'][number]) => task.assignees.some((row) => row.personId === personId) ||
    (task.assignees.length === 0 && task.executorId === personId);
  const workable = content.tasks.filter((task) => !['UNASSIGNED', 'VOID'].includes(task.status) &&
    (canManage || isOwnTask(task)));
  const [taskId, setTaskId] = useState(workable.find((task) => task.assignees.some((row) => row.personId === personId))?.taskId ??
    workable[0]?.taskId);
  const task = content.tasks.find((row) => row.taskId === taskId);
  const [mode, setMode] = useState<'FILE' | 'LINK' | 'MARKDOWN'>('MARKDOWN');
  const [file, setFile] = useState<File | null>(null);
  const [link, setLink] = useState({ title: '', url: '', description: '' });
  const [markdown, setMarkdown] = useState('');
  const [documentId, setDocumentId] = useState<string>();
  const [draftVersion, setDraftVersion] = useState(0);
  const [documentTitle, setDocumentTitle] = useState('策划脚本');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const query = useQuery({ queryKey: ['content-materials', accountId, projectId, content.contentId],
    queryFn: () => listContentMaterials(token, projectId, content.contentId), enabled: !!token && !!content.contentId });

  async function refresh() {
    await client.invalidateQueries({ queryKey: ['content-materials', accountId, projectId, content.contentId] });
  }

  async function saveFile() {
    if (!file || !task) return;
    setBusy(true); setMessage(null);
    try {
      const staged = await stageTaskMaterial({ token, projectId, packageId, contentId: content.contentId,
        taskId: task.taskId, expectedVersion: task.version, contentType,
        key: `material-stage-${crypto.randomUUID()}`, file });
      await addFileMaterial({ token, projectId, taskId: task.taskId, key: `material-file-${crypto.randomUUID()}`,
        material: { taskVersion: task.version, stagingId: staged.stagingId, title: file.name, description: null } });
      setFile(null); setMessage({ type: 'success', text: '文件已保存为任务材料，任务状态未改变。' }); await refresh();
    } catch (failure) { setMessage({ type: 'error', text: failureMessage(failure) }); }
    finally { setBusy(false); }
  }

  async function saveLink() {
    if (!task || !link.title.trim() || !link.url.trim()) return;
    setBusy(true); setMessage(null);
    try { await addLinkMaterial({ token, projectId, taskId: task.taskId, key: `material-link-${crypto.randomUUID()}`,
      material: { taskVersion: task.version, title: link.title.trim(), url: link.url.trim(),
        description: link.description.trim() || null } });
      setLink({ title: '', url: '', description: '' });
      setMessage({ type: 'success', text: '外部链接已保存，任务状态未改变。' }); await refresh();
    } catch (failure) { setMessage({ type: 'error', text: failureMessage(failure) }); }
    finally { setBusy(false); }
  }

  async function saveDraft() {
    if (!task) return;
    setBusy(true); setMessage(null);
    try { const result = await saveMarkdownDraft({ token, projectId, taskId: task.taskId,
      key: `material-markdown-${crypto.randomUUID()}`, material: { expectedDraftVersion: draftVersion,
        ...(documentId ? { materialId: documentId } : { title: documentTitle.trim() || '策划脚本' }), markdown } });
      setDocumentId(result.material.materialId); setDraftVersion(result.material.markdown?.draftVersion ?? draftVersion + 1);
      setMessage({ type: 'success', text: `Markdown 草稿 V${result.material.markdown?.draftVersion ?? ''} 已保存，任务状态未改变。` });
      await refresh();
    } catch (failure) { setMessage({ type: 'error', text: failureMessage(failure) }); }
    finally { setBusy(false); }
  }

  async function publish() {
    if (!task || !documentId || draftVersion < 1) return;
    setBusy(true); setMessage(null);
    try { const result = await publishMarkdownVersion({ token, projectId, taskId: task.taskId,
      key: `material-publish-${crypto.randomUUID()}`,
      material: { materialId: documentId, expectedDraftVersion: draftVersion } });
      const versionNo = result.material.markdown?.versions.at(-1)?.versionNo;
      setMessage({ type: 'success', text: `Markdown 正式版本 V${versionNo} 已发布，旧版本保持不变。` }); await refresh();
    } catch (failure) { setMessage({ type: 'error', text: failureMessage(failure) }); }
    finally { setBusy(false); }
  }

  async function openFile(path: string, filename: string, download: boolean) {
    setBusy(true); setMessage(null);
    try {
      const blob = await downloadContentMaterial(token, path);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      if (download) anchor.download = filename;
      else { anchor.target = '_blank'; anchor.rel = 'noopener noreferrer'; }
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (failure) { setMessage({ type: 'error', text: failureMessage(failure) }); }
    finally { setBusy(false); }
  }

  function openDocument(id: string) {
    const material = query.data?.items.find((item) => item.materialId === id);
    if (!material?.markdown) return;
    setDocumentId(id); setDocumentTitle(material.title); setMarkdown(material.markdown.draft);
    setDraftVersion(material.markdown.draftVersion); setMode('MARKDOWN'); setMessage(null);
  }

  async function removeMaterial(materialId: string, version: number) {
    setBusy(true); setMessage(null);
    try {
      await deleteContentMaterial({ token, projectId, contentId: content.contentId, materialId,
        key: `material-delete-${crypto.randomUUID()}`, change: { expectedVersion: version, reason: '内容材料软删除' } });
      if (documentId === materialId) { setDocumentId(undefined); setDraftVersion(0); setMarkdown(''); }
      setMessage({ type: 'success', text: '材料已软删除，审计记录已保留。' });
      await refresh();
    } catch (failure) { setMessage({ type: 'error', text: failureMessage(failure) }); }
    finally { setBusy(false); }
  }

  return <div className="task-materials-panel">
    {!task && <div className="issue-empty">当前没有可作为来源的制作任务；已有内容材料仍可查看，新增材料需先完成任务分派。</div>}
    <div className="material-toolbar">{workable.length > 0 && <Select value={taskId} onChange={(value) => { setTaskId(value); setDocumentId(undefined);
      setDraftVersion(0); setMarkdown(''); }} options={workable.map((row) => ({ value: row.taskId,
        label: `${row.taskName} · ${taskStatusLabel(row.status)}` }))} />}
      <div>{(['MARKDOWN', 'LINK', 'FILE'] as const).map((value) => <button key={value}
        className={mode === value ? 'is-active' : ''} onClick={() => setMode(value)}>
        {value === 'MARKDOWN' ? 'Markdown' : value === 'LINK' ? '外部链接' : '附件文件'}</button>)}</div></div>
    {message && <Alert type={message.type} showIcon title={message.text} closable onClose={() => setMessage(null)} />}
    {mode === 'FILE' && task && <div className="material-editor-row"><input type="file"
      accept="video/mp4,.mp4,application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,text/plain,.txt,text/markdown,.md,image/png,.png,image/jpeg,.jpg,.jpeg"
      onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
      <Button type="primary" disabled={!file} loading={busy} onClick={() => void saveFile()}>保存文件</Button></div>}
    {mode === 'LINK' && task && <div className="material-link-form"><Input placeholder="链接标题" value={link.title}
      onChange={(event) => setLink({ ...link, title: event.target.value })} /><Input placeholder="https://"
      value={link.url} onChange={(event) => setLink({ ...link, url: event.target.value })} />
      <Input placeholder="说明（可选）" value={link.description}
        onChange={(event) => setLink({ ...link, description: event.target.value })} />
      <Button type="primary" loading={busy} disabled={!link.title.trim() || !link.url.trim()}
        onClick={() => void saveLink()}>保存链接</Button></div>}
    {mode === 'MARKDOWN' && <><div className="markdown-document-tabs"><button className={!documentId ? 'is-active' : ''}
      disabled={!task}
      onClick={() => { setDocumentId(undefined); setDocumentTitle('策划脚本'); setMarkdown(''); setDraftVersion(0); }}><Icon name="plus" size={16} strokeWidth={2} /> 新建文档</button>
      {query.data?.items.filter((item) => item.kind === 'MARKDOWN').map((item) => <button key={item.materialId}
        className={documentId === item.materialId ? 'is-active' : ''} onClick={() => openDocument(item.materialId)}>
        {item.title}<small>{item.sourceTaskName ?? item.sourceTaskCode ?? '内容级'} · V{item.markdown?.versions.length ?? 0}</small></button>)}</div>
      <Input value={documentTitle} disabled={!!documentId || !task} onChange={(event) => setDocumentTitle(event.target.value)}
        placeholder="文档名称" />
      <div className="markdown-editor-grid"><Input.TextArea rows={12} value={markdown}
        disabled={!task || (!!documentId && query.data?.items.find((item) => item.materialId === documentId)?.canEdit !== true)}
        onChange={(event) => setMarkdown(event.target.value)} placeholder="# 策划脚本\n\n在这里记录分镜、口播或执行说明…" />
        <MarkdownPreview value={markdown} /></div><div className="material-actions"><span>草稿 V{draftVersion || '新建'} · 正式版本只追加不覆盖</span>
        <Button loading={busy} disabled={!task || (!!documentId && query.data?.items.find((item) => item.materialId === documentId)?.canEdit !== true)}
          onClick={() => void saveDraft()}>保存草稿</Button>
        <Button type="primary" disabled={!task || !documentId || !markdown.trim() ||
          query.data?.items.find((item) => item.materialId === documentId)?.canEdit !== true} loading={busy}
          onClick={() => void publish()}>发布正式版本</Button></div></>}
    <div className="material-history"><strong>已保存材料 {query.data?.items.length ?? 0}</strong>
      {query.data?.items.filter((item) => item.kind !== 'MARKDOWN').map((item) => <article key={item.materialId}>
        <Tag>{item.kind === 'FILE' ? '文件' : '链接'}</Tag><span><b>{item.title}</b><small>{item.kind === 'FILE' ?
          item.file?.originalFilename : `${item.linkVersions.length} 个版本`} · 来源：{item.sourceTaskName ?? item.sourceTaskCode ?? '内容级'}</small></span>
        {item.kind === 'FILE' && item.file && item.downloadPath && <div className="material-history-actions">
          <Button size="small" disabled={busy} onClick={() => void openFile(item.downloadPath!,
            item.file!.originalFilename, false)}>预览</Button>
          <Button size="small" disabled={busy} onClick={() => void openFile(item.downloadPath!,
            item.file!.originalFilename, true)}>下载</Button></div>}
        {item.kind === 'LINK' && item.linkVersions.at(-1) && <Button size="small" href={item.linkVersions.at(-1)!.url}
          target="_blank" rel="noopener noreferrer">打开链接</Button>}
        {item.canDelete && <Popconfirm title="删除这份材料？" description="将软删除并保留审计记录。" okText="删除"
          cancelText="取消" onConfirm={() => void removeMaterial(item.materialId, item.version)}>
          <Button size="small" danger loading={busy}>删除</Button></Popconfirm>}</article>)}</div>
  </div>;
}

export function ContentDetailPanel({ token, accountId, personId, projectId, packageId, packageLabel, tierName, content,
  contentType, canManage, isAdmin, canAssignTasks, canProgressTasks, canProgressOthers, canReturnSubmittedTasks,
  canResolveIssues, executorOptions,
  taskOptions, tiers, onAssign }: { token: string;
  accountId: string | null; personId: string | null; projectId: string; packageId: string;
  packageLabel?: string | undefined; tierName?: string | undefined;
  content: VideoContentSummary; contentType: PackagePageContentType; canManage: boolean; isAdmin: boolean;
  canAssignTasks: boolean;
  canProgressTasks: boolean; canProgressOthers?: boolean | undefined; canReturnSubmittedTasks: boolean;
  canResolveIssues: boolean;
  executorOptions?: Array<{ personId: string; label: string }> | undefined;
  taskOptions?: Array<{ code: string; name: string }> | undefined;
  tiers?: Array<{ code: string; name: string; count: number; used: number }> | undefined;
  onAssign?: ((content: VideoContentSummary) => void) | undefined }) {
  const client = useQueryClient();
  // BUG-0168：「安排发布计划」要带上当前页面的筛选/搜索上下文，返回时才能回到原位。
  const location = useLocation();
  const [pageParams] = useSearchParams();
  const publicationSource = location.pathname.endsWith('/content') ? 'content' as const : 'package' as const;
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(content.title ?? '');
  const [legacyNoDraft, setLegacyNoDraft] = useState(content.legacyNo ?? '');
  const [displayNoDraft, setDisplayNoDraft] = useState(content.displayNo?.toString() ?? '');
  const [titleBusy, setTitleBusy] = useState(false);
  const [bulkProgressBusy, setBulkProgressBusy] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [variantOpen, setVariantOpen] = useState(false);
  const [variantTarget, setVariantTarget] = useState(content.tierCode ?? '');
  const [variantReason, setVariantReason] = useState('');
  const [variantBusy, setVariantBusy] = useState(false);
  const [variantError, setVariantError] = useState<string | null>(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('ASSIGNMENT');
  const assigned = content.tasks.filter((task) => task.status !== 'UNASSIGNED').length;
  const unassigned = content.tasks.length - assigned;
  const completedCount = content.tasks.filter((task) => task.status === 'COMPLETED').length +
    (content.publicationTask?.status === 'COMPLETED' ? 1 : 0);
  const fallbackTitle = `待确定${contentType === 'VIDEO' ? '视频' : '文章'}主题`;
  const tabsId = `content-tabs-${content.contentId}`;
  const nextStep = describeContentNextStep({ status: content.status, canManage, canAssignTasks,
    hasTier: !!content.tierCode, unassigned });

  function focusResult() {
    setActiveTab('DELIVERY');
    document.getElementById(tabsId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function focusAssignment() {
    setActiveTab('ASSIGNMENT');
    document.getElementById(tabsId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function submitVariantChange() {
    const reason = variantReason.trim();
    if (!variantTarget || reason.length < 2 || variantBusy) return;
    setVariantBusy(true); setVariantError(null);
    try {
      const preview = await previewBusinessChange({ token, projectId, kind: 'VARIANT_CHANGE',
        targetId: content.contentId, expectedVersion: content.version, reason });
      await changeContentVariant({ token, projectId, contentId: content.contentId,
        key: `content-variant-${crypto.randomUUID()}`, change: { expectedVersion: content.version,
          previewId: preview.previewId, previewVersion: preview.previewVersion, reason, targetVariantKey: variantTarget } });
      setVariantOpen(false); setVariantReason('');
      await client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] });
      await client.invalidateQueries({ queryKey: ['content-capacity', contentType, accountId, projectId, packageId] });
    } catch (failure) { setVariantError(failureMessage(failure)); }
    finally { setVariantBusy(false); }
  }

  async function saveTitle() {
    const title = titleDraft.trim();
    const legacyNo = legacyNoDraft.trim();
    const displayNoText = displayNoDraft.trim();
    const displayNo = displayNoText ? Number(displayNoText) : undefined;
    if (!title || title.length > 160 || (content.legacyNo !== null && !/^[0-9]{1,9}$/.test(legacyNo)) ||
      (displayNoText && (!Number.isSafeInteger(displayNo) || displayNo! < 1 || displayNo! > 999_999_999)) || titleBusy) return;
    setTitleBusy(true); setTitleError(null);
    try {
      await updateContentTitle({ token, projectId, packageId, contentId: content.contentId, contentType,
        expectedVersion: content.version, title,
        ...(content.legacyNo === null ? {} : { legacyNo }), ...(displayNo === undefined ? {} : { displayNo }),
        key: `content-title-${crypto.randomUUID()}` });
      setEditingTitle(false);
      await client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] });
    } catch (failure) { setTitleError(failureMessage(failure)); }
    finally { setTitleBusy(false); }
  }

  async function completeAllTasks() {
    if (bulkProgressBusy) return;
    setBulkProgressBusy(true); setTitleError(null);
    try {
      const pending = content.tasks.filter((row) => ['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS'].includes(row.status));
      for (const task of pending) {
        await transitionTaskV2({ token, projectId, taskId: task.taskId, action: 'complete',
          change: { expectedVersion: task.version }, key: `task-complete-${crypto.randomUUID()}` });
      }
      await client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] });
    } catch (failure) { setTitleError(failureMessage(failure)); }
    finally { setBulkProgressBusy(false); }
  }

  function removeContent() {
    let reason = '';
    Modal.confirm({ title: '删除这条未交付内容？', okText: '确认删除', okButtonProps: { danger: true },
      content: <div><p>删除后内容与分工任务一并作废，占用的规格容量会释放；此操作不可撤销。</p>
        <Input.TextArea rows={3} placeholder="填写删除原因" onChange={(event) => { reason = event.target.value; }} /></div>,
      onOk: async () => {
        if (reason.trim().length < 2) {
          void antMessage.error('请填写至少 2 个字的删除原因');
          throw new Error('请填写至少 2 个字的删除原因');
        }
        setVoidBusy(true); setVoidError(null);
        try {
          await voidContent({ token, projectId, packageId, contentId: content.contentId,
            key: `content-void-${crypto.randomUUID()}`,
            change: { expectedVersion: content.version, reason: reason.trim() } });
          await Promise.all([client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] }),
            client.invalidateQueries({ queryKey: ['content-capacity', accountId, projectId, packageId] })]);
        } catch (failure) {
          const message = failureMessage(failure); setVoidError(message); void antMessage.error(message); throw failure;
        }
        finally { setVoidBusy(false); }
      } });
  }

  async function copyContentCode() {
    try {
      await navigator.clipboard.writeText(content.contentCode);
      antMessage.success('内容编号已复制');
    } catch {
      antMessage.info(content.contentCode);
    }
  }

  return <article className="content-focus-panel">
    <header className="content-focus-head">
      <div className="content-focus-main">
        <div className="content-focus-meta"><span>{contentType === 'VIDEO' ? '视频' : '文章'}</span><i>·</i>
          {packageLabel && <><span>{packageLabel}</span><i>·</i></>}
          <span>{tierName ?? (content.tierCode ? '已选规格' : '未选档')}</span>
          <Tooltip title="复制系统内部编号"><button type="button" className="content-copy-code"
            onClick={() => void copyContentCode()}>编号 ⧉</button></Tooltip></div>
        <div className={`content-title-line ${content.title ? '' : 'is-empty'}`}><h2>{contentTitle(content.displayNo, content.legacyNo, content.title ?? fallbackTitle)}</h2>
          <Tag color={content.status === 'DELIVERED' ? 'green' : content.status === 'PENDING_DELIVERY' ? 'gold' : 'processing'}>
            {contentStatusLabel(content.status)}</Tag>
          {((content.status === 'DELIVERED' && (isAdmin || (canManage && content.legacyNo !== null))) ||
            (content.status !== 'DELIVERED' && (canManage || canProgressOthers))) && content.status !== 'VOID' && !editingTitle &&
            <Button size="small" onClick={() => { setTitleDraft(content.title ?? '');
              setLegacyNoDraft(content.legacyNo ?? ''); setDisplayNoDraft(content.displayNo?.toString() ?? '');
              setTitleError(null); setEditingTitle(true); }}>
              {content.status === 'DELIVERED' ? (isAdmin ? '更正内容序号' : '更正历史编号') : '编辑标题与序号'}</Button>}
          {canManage && !['DELIVERED', 'VOID'].includes(content.status) && !editingTitle &&
            <Button size="small" danger loading={voidBusy} onClick={removeContent}>删除内容</Button>}</div>
        {editingTitle && <div className="content-title-editor"><div className="content-metadata-fields">
          <label><span>内容序号{content.displayNoLocked && !isAdmin ? '（已交付锁定）' : ''}</span><Input value={displayNoDraft}
            maxLength={9} inputMode="numeric" disabled={!!content.displayNoLocked && !isAdmin}
            status={displayNoDraft && !/^[1-9][0-9]{0,8}$/.test(displayNoDraft) ? 'error' : ''}
            placeholder={content.displayNo === null ? '历史异常，可留空' : '输入 1–9 位正整数'} onChange={(event) => {
              setDisplayNoDraft(event.target.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 9)); setTitleError(null);
            }} onPressEnter={() => void saveTitle()} /></label>
          {content.legacyNo !== null && <label><span>历史业务编号</span><Input autoFocus value={legacyNoDraft} maxLength={9} inputMode="numeric" disabled={!canManage}
            status={legacyNoDraft && !/^[0-9]{1,9}$/.test(legacyNoDraft) ? 'error' : ''}
            placeholder="输入 1–9 位数字" onChange={(event) => {
              setLegacyNoDraft(event.target.value.replace(/\D/g, '').slice(0, 9)); setTitleError(null);
            }} onPressEnter={() => void saveTitle()} /></label>}
          <label><span>内容标题</span><Input autoFocus={content.legacyNo === null} value={titleDraft} maxLength={160} showCount
            disabled={content.status === 'DELIVERED' || content.status === 'VOID'}
            placeholder={`输入${contentType === 'VIDEO' ? '视频' : '文章'}主题`}
            onChange={(event) => { setTitleDraft(event.target.value); setTitleError(null); }}
            onPressEnter={() => void saveTitle()} /></label></div><Button type="primary" loading={titleBusy}
          disabled={!titleDraft.trim() || (content.legacyNo !== null && !/^[0-9]{1,9}$/.test(legacyNoDraft.trim())) ||
            (!!displayNoDraft.trim() && !/^[1-9][0-9]{0,8}$/.test(displayNoDraft.trim()))}
          onClick={() => void saveTitle()}>保存更正</Button>
          <Button disabled={titleBusy} onClick={() => { setEditingTitle(false); setTitleError(null); }}>取消</Button></div>}
        {titleError && <Alert className="content-title-error" type="error" showIcon title={titleError} />}
        {voidError && <Alert className="content-title-error" type="error" showIcon title={voidError} />}</div>
    </header>
    <section className={`content-next-step is-${content.status.toLowerCase()}`}><div><b>下一步</b>
      <strong>{nextStep.title}</strong><span>{nextStep.description}</span></div>
      {nextStep.action === 'ASSIGN' && canAssignTasks && onAssign && !['DELIVERED', 'VOID'].includes(content.status) ?
        <Button type="primary" onClick={() => onAssign(content)}>{content.tierCode ? `补齐分工（${unassigned}）` : '开始配置'}</Button> :
        nextStep.action === 'REVIEW' ? <Button type="primary" onClick={focusResult}>前往交付登记</Button> :
        nextStep.action === 'WAITING_REVIEW' ? <Button onClick={focusResult}>查看交付进度</Button> :
        <Button onClick={nextStep.action === 'VIEW_DELIVERED' ? focusResult : focusAssignment}>
          {nextStep.action === 'VIEW_DELIVERED' ? '查看交付成果' : '查看分工进度'}</Button>}</section>
    <div className="content-focus-metrics"><div><small>制作分工</small><strong>{content.tasks.length ? `${assigned}/${content.tasks.length}` : '未设置'}</strong></div>
      <div><small>已完成任务</small><strong>{completedCount}</strong></div>
      <div><small>当前状态</small><strong>{contentStatusLabel(content.status)}</strong></div>
      <div><small>内容版本</small><strong>V{content.version}</strong></div></div>
    <div id={tabsId} className="content-focus-tabs"><Tabs activeKey={activeTab} onChange={setActiveTab} items={[
      { key: 'ASSIGNMENT', label: '01 · 制作分工', children: <section className="content-focus-section">
        <div className="focus-section-title"><div><h3>{canAssignTasks ? '制作分工' : '内容制作分工'}</h3></div>
          <div className="focus-section-side">{!canAssignTasks && <p>只读查看完整分工；你可以完成本人任务，金额与收款信息仅对负责人可见</p>}
            {canAssignTasks && <Tooltip title="已完成任务在内容交付前仍可纠正负责人；交付后锁定"><span className="focus-section-help" tabIndex={0}
              aria-label="子任务无需独立上传成果或验收">ⓘ 说明</span></Tooltip>}
            {canAssignTasks && onAssign && !['DELIVERED', 'VOID'].includes(content.status) && (!content.tierCode || unassigned > 0) &&
              <Button size="small" onClick={() => onAssign(content)}>{content.tierCode ? '补齐负责人' : '配置分工'}</Button>}
            {canProgressOthers && !['DELIVERED', 'VOID'].includes(content.status) &&
              content.tasks.some((task) => ['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS'].includes(task.status)) &&
              <Button size="small" type="primary" ghost loading={bulkProgressBusy}
                onClick={() => void completeAllTasks()}>全部标记完成</Button>}</div></div>
        {content.tasks.length === 0 && <div className="issue-empty">{canAssignTasks ? '选档后会生成标准制作任务。' : '这条内容暂无制作任务。'}</div>}
        <div className="content-subtask-list">{content.tasks.map((task) => <ContentTaskRow key={task.taskId} token={token}
          accountId={accountId} personId={personId} projectId={projectId} packageId={packageId} content={content}
          task={{ ...task, taskName: task.taskName || taskLabel(task.taskCode, taskOptions) }} contentType={contentType}
          canManage={canManage} canAssignTasks={canAssignTasks} canProgressTasks={canProgressTasks}
          canProgressOthers={canProgressOthers} canReturnSubmittedTasks={canReturnSubmittedTasks}
          executorOptions={executorOptions} />)}
          {content.publicationTask && <div className="content-subtask-row publication-task-row">
            <div className="content-subtask-copy"><strong>发布</strong><small>{
              content.publicationTask.mode === 'CUSTOMER' ? '客户自行发布' :
              content.publicationTask.assignees.length ? content.publicationTask.assignees.map((row) =>
                `${row.displayName}${row.role === 'PRIMARY' ? '（主责）' : ''}`).join(' / ') : '待安排发布人'
            }{content.publicationTask.lockedAmount !== null ? ` · 发布费 ¥${content.publicationTask.lockedAmount}` : ''}</small></div>
            <Tag color={content.publicationTask.status === 'COMPLETED' ? 'green' :
              content.publicationTask.status === 'VOID' ? 'default' : content.publicationTask.status === 'UNPLANNED' ? 'gold' : 'blue'}>
              {PUBLICATION_TASK_STATUS_LABEL[content.publicationTask.status]}</Tag>
            <Link to={publicationPlanPath({ projectId, contentId: content.contentId, packageId,
              status: pageParams.get('status') ?? undefined, q: pageParams.get('q') ?? undefined,
              from: publicationSource })}><Button size="small">安排发布计划</Button></Link>
            <Link to={`/projects/${projectId}/publications?content=${content.contentId}`}><Button size="small">发布详情</Button></Link>
            {content.publicationTask.status !== 'COMPLETED' && content.publicationTask.status !== 'VOID' &&
              <Link to={`/projects/${projectId}/publications?content=${content.contentId}&action=record`}>
                <Button size="small" type={content.status === 'DELIVERED' ? 'primary' : 'default'}>去登记发布结果</Button></Link>}
          </div>}</div>
        <ContentPublicationInfoPanel token={token} accountId={accountId} projectId={projectId} packageId={packageId}
          contentType={contentType} content={content} canEdit={canManage || !!canProgressOthers} />
      </section> },
      { key: 'MATERIAL', label: '02 · 材料与版本', children: <section className="content-focus-section">
        <div className="focus-section-title"><div><span>02</span><h3>材料与版本</h3></div>
          <p>文件、外部链接和 Markdown 都是过程材料，保存不改变任务状态</p></div>
        <TaskMaterialsPanel token={token} accountId={accountId} personId={personId} projectId={projectId}
          packageId={packageId} content={content} contentType={contentType} canManage={canManage} /></section> },
      { key: 'ISSUES', label: '03 · 问题与修改', children: <section className="content-focus-section">
        <div className="focus-section-title"><div><span>03</span><h3>问题与修改</h3></div>
          <p>提交修改意见、标注位置并分派处理人</p></div>
        <ContentIssuesPanel token={token} accountId={accountId} personId={personId} projectId={projectId}
          packageId={packageId} content={content} contentType={contentType} canManage={canManage}
          canResolveIssues={canResolveIssues}
          executorOptions={executorOptions} /></section> },
      { key: 'CHANGE', label: '04 · 变更与异常', children: <section className="content-focus-section">
        <div className="focus-section-title"><div><span>04</span><h3>变更与异常</h3></div>
          <p>撤回、更正和作废统一进入项目变更记录</p></div>
        <div className="content-delivery-gateway"><div><strong>高影响操作集中处理</strong>
          <span>系统会先展示任务、人员、金额和结算影响，再允许在原业务对象上确认。一般异常可先提交为问题。</span></div>
          <Space wrap><Button type="primary" onClick={() => setActiveTab('ISSUES')}>提交异常或修改意见</Button>
            <Link to={`/projects/${projectId}/changes`}><Button>查看变更记录</Button></Link></Space></div>
        <div className="content-variant-row"><div><strong>结算档位</strong>
          <span>{tierName ?? (content.tierCode ? content.tierCode : '未选档')}{content.tierCode ? `（${content.tierCode}）` : ''}</span>
          <small>已生成结算单的内容需先撤销结算单，才能修改档位。</small></div>
          {canManage && (tiers?.length ?? 0) > 1 && <Button onClick={() => { setVariantTarget(content.tierCode ?? ''); setVariantReason(''); setVariantError(null); setVariantOpen(true); }}>修改结算档位</Button>}
        </div></section> },
      { key: 'DELIVERY', label: '05 · 交付登记', children: <section className="content-focus-section">
        <div className="focus-section-title"><div><span>05</span><h3>整条内容交付登记</h3></div>
          <p>这里不再选择或提交过程文件，交付只登记整条内容已经成功交付的事实</p></div>
        <div className="content-delivery-gateway"><div><strong>{content.status === 'DELIVERED' ? '这条内容已有有效交付记录' : '前往项目交付中心登记'}</strong>
          <span>登记时必须上传成功证据，并分别记录实际交付日与系统登记时间；撤回后再次交付会复用同一费用组。</span></div>
          <Link to={`/projects/${projectId}/deliveries?content=${content.contentId}`}><Button type="primary">
            {content.status === 'DELIVERED' ? '查看交付记录' : canManage ? '登记整条内容交付' : '查看交付进度'}</Button></Link></div></section> },
    ]} /></div>
    <footer className="content-focus-audit"><VideoAuditPanel token={token} accountId={accountId} projectId={projectId}
      packageId={packageId} contentId={content.contentId} contentType={contentType} /></footer>
    <Modal title="修改结算档位" open={variantOpen} confirmLoading={variantBusy} okText="确认修改"
      onCancel={() => { if (!variantBusy) setVariantOpen(false); }} onOk={() => void submitVariantChange()}>
      <div className="ops-form-note">修改档位会重新生成制作分工并影响费用与结算；已生成结算单的内容需先撤销结算单。</div>
      {variantError && <Alert type="error" showIcon title={variantError} />}
      <label className="content-variant-field">目标档位
        <Select value={variantTarget || null} onChange={(next) => setVariantTarget(typeof next === 'string' ? next : '')} placeholder="选择结算档位"
          options={(tiers ?? []).map((tier) => ({ value: tier.code, label: `${tier.name}（${tier.code}） · 已用 ${tier.used}/${tier.count}` }))} />
      </label>
      <label className="content-variant-field">变更原因
        <Input.TextArea rows={3} value={variantReason} onChange={(event) => setVariantReason(event.target.value)}
          placeholder="说明为什么要调整结算档位（至少 2 个字）" />
      </label>
    </Modal>
  </article>;
}

function MyIssueQueue({ token, accountId, projectId }: { token: string; accountId: string | null; projectId: string }) {
  const issues = useQuery({ queryKey: ['my-content-issues', accountId], queryFn: () => listMyContentIssues(token),
    enabled: !!token && !!accountId });
  if (!issues.data?.items.length) return null;
  return <section className="my-issue-queue"><header><div><span className="panel-number">CROSS-PROJECT FIXES</span>
    <h2>我的待修复</h2></div><b>{issues.data.items.length}</b></header>
    <div>{issues.data.items.slice(0, 8).map((issue) => <Link key={issue.issueId}
      className={issue.projectId === projectId ? 'is-current' : ''}
      to={`/projects/${issue.projectId}/${issue.contentType === 'ARTICLE' ? 'article' : 'video'}-packages`}>
      <span>{issue.blocking ? '阻断' : ISSUE_CATEGORY[issue.category]}</span><strong>{issue.title}</strong>
      <small>{issue.packageCode} · {issue.contentCode}</small></Link>)}</div>
  </section>;
}

/**
 * 服务包页面。视频包与文章包共用同一套命令与契约，差异只有三处：
 * 路由前缀、界面用词（视频/文章、成片/稿件），以及 options 查询要带上内容类型。
 * 因此这里用一个 contentType 属性参数化，而不是再复制一份 400 行页面。
 */
export type PackagePageContentType = 'VIDEO' | 'ARTICLE';

/** 编辑抽屉按服务类型分组：同一个 groupName 只在第一行显示一次组头。 */
export function definitionGroupMeta(variants: ReadonlyArray<{ groupName?: string | null | undefined }>,
  index: number): { name: string; count: number } | null {
  const name = variants[index]?.groupName?.trim();
  if (!name) return null;
  if (index > 0 && variants[index - 1]?.groupName?.trim() === name) return null;
  let count = 0;
  for (let cursor = index; cursor < variants.length && variants[cursor]?.groupName?.trim() === name; cursor += 1) count += 1;
  return { name, count };
}

const COPY = {
  VIDEO: { title: '视频服务包', unit: '条', result: '成片文件' },
  ARTICLE: { title: '文章服务包', unit: '篇', result: '稿件文件' },
} as const;

/**
 * 服务包表格视图（BUG-0093）。管理层与服务包负责人、纯执行者两个分支共用同一份行数据，
 * 执行者缺少的容量、预算和负责人字段降级为“—”，不额外请求财务数据。
 */
export type PackageStatusFilter = 'ALL' | 'ACTIVE' | 'COMPLETED' | 'DRAFT';

export const PACKAGE_STATUS_FILTERS: ReadonlyArray<{ value: PackageStatusFilter; label: string }> = [
  { value: 'ALL', label: '全部' },
  { value: 'ACTIVE', label: '执行中' },
  { value: 'COMPLETED', label: '已完成' },
  { value: 'DRAFT', label: '草稿' },
];

export interface PackageTableRow {
  packageId: string;
  packageCode: string;
  title: string;
  status: VideoPackageSummary['status'];
  statusLabel: string;
  contentTypeLabel: string;
  period: string;
  capacityLabel: string;
  budgetLabel: string;
  owner: string;
  searchText: string;
}

export interface PackageTableSource {
  packageId: string;
  packageCode: string;
  status: VideoPackageSummary['status'];
  templateName: string;
  packageTitle: string | null;
  contentType?: PackagePageContentType | undefined;
  periodFrom: string;
  periodUntil: string;
  capacity?: number | undefined;
  used?: number | undefined;
  budget?: string | undefined;
  /** BUG-0176：服务端掩码金额时列表列也显示「无查看权限」，不再显示假 ¥0.00。 */
  amountsHidden?: boolean | undefined;
  owner?: string | undefined;
}

function formatPackageAmount(value: string): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return `¥${value}`;
  return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function buildPackageTableRows(sources: PackageTableSource[]): PackageTableRow[] {
  return sources.map((source) => {
    const title = source.packageTitle ?? source.templateName;
    const statusLabel = PACKAGE_STATUS_LABEL[source.status];
    return {
      packageId: source.packageId,
      packageCode: source.packageCode,
      title,
      status: source.status,
      statusLabel,
      contentTypeLabel: source.contentType === 'ARTICLE' ? '文章' : '视频',
      period: `${source.periodFrom} ~ ${source.periodUntil}`,
      capacityLabel: typeof source.capacity === 'number' ? `${source.capacity} / ${source.used ?? 0}` : '—',
      budgetLabel: source.amountsHidden ? '无查看权限'
        : source.budget ? formatPackageAmount(source.budget) : '—',
      owner: source.owner ?? '—',
      searchText: [title, source.packageTitle, source.templateName, source.packageCode, statusLabel, source.owner]
        .filter(Boolean).join(' '),
    };
  });
}

/** tab 只是生命周期的快捷筛选：草稿含已退回，执行中含已提交/已确认等尚未完成的状态。 */
export function matchesPackageStatusFilter(status: VideoPackageSummary['status'],
  filter: PackageStatusFilter): boolean {
  if (filter === 'ALL') return true;
  if (filter === 'COMPLETED') return status === 'COMPLETED';
  if (filter === 'DRAFT') return status === 'DRAFT' || status === 'RETURNED';
  return status === 'SUBMITTED' || status === 'CONFIRMED' || status === 'ACTIVE';
}

export function filterPackageTableRows(rows: PackageTableRow[], filter: PackageStatusFilter,
  search: string): PackageTableRow[] {
  const query = search.trim().toLowerCase();
  return rows.filter((row) => matchesPackageStatusFilter(row.status, filter) &&
    (!query || row.searchText.toLowerCase().includes(query)));
}

export function packageFilterCount(rows: PackageTableRow[], filter: PackageStatusFilter): number {
  return rows.filter((row) => matchesPackageStatusFilter(row.status, filter)).length;
}

function packageStatusColor(status: VideoPackageSummary['status']): string {
  return status === 'ACTIVE' ? 'blue' : status === 'COMPLETED' ? 'green' :
    status === 'RETURNED' ? 'red' : status === 'DRAFT' ? 'default' : 'gold';
}

const PACKAGE_TABLE_COLUMNS: TableColumnsType<PackageTableRow> = [
  { title: '服务包', dataIndex: 'title', key: 'title', width: 300,
    render: (_value, row) => <div className="content-table-name">
      <span className={`content-table-type ${row.contentTypeLabel === '文章' ? 'is-article' : ''}`}>
        {row.contentTypeLabel === '文章' ? '文' : '视'}</span>
      <span className="content-table-title" title={`${row.title} · ${row.packageCode}`}>
        {row.title}<br /><small className="content-table-muted">{row.packageCode}</small></span>
    </div> },
  { title: '状态', dataIndex: 'statusLabel', key: 'status', width: 104,
    render: (label: string, row) => <Tag color={packageStatusColor(row.status)}>{label}</Tag> },
  { title: '内容类型', dataIndex: 'contentTypeLabel', key: 'contentType', width: 100 },
  { title: '周期', dataIndex: 'period', key: 'period', width: 200,
    render: (period: string) => <span className="content-table-date">{period}</span> },
  { title: '容量 / 已用', dataIndex: 'capacityLabel', key: 'capacity', width: 120,
    render: (value: string) => <span className="content-table-muted">{value}</span> },
  { title: '预算', dataIndex: 'budgetLabel', key: 'budget', width: 130, align: 'right',
    render: (value: string) => <strong>{value}</strong> },
  { title: '负责人', dataIndex: 'owner', key: 'owner', width: 110,
    render: (value: string) => <span className="content-table-person">{value}</span> },
];

const CONTENT_COLUMNS = [
  { key: 'PLACEHOLDER', label: '待分派' },
  { key: 'IN_PROGRESS', label: '制作中' },
  { key: 'PENDING_DELIVERY', label: '待确认' },
  { key: 'DELIVERED', label: '已交付' },
] as const;

function contentColumn(status: string): string {
  if (status === 'PLACEHOLDER' || status === 'PLANNED' || status === 'TIERED') return 'PLACEHOLDER';
  if (status === 'PENDING_DELIVERY') return 'PENDING_DELIVERY';
  if (status === 'DELIVERED') return 'DELIVERED';
  return 'IN_PROGRESS';
}

function PackageContentList({ projectId, packageId, contents, contentType, canManage, personId, returnedContentId,
  statusContext, qContext }: {
  projectId: string; packageId: string; contents: VideoContentSummary[];
  contentType: PackagePageContentType; canManage: boolean; personId?: string | null;
  returnedContentId?: string | null; statusContext?: string | null; qContext?: string | null }) {
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();
  const visible = contents.filter((content) => (statusFilter === 'ALL' || contentColumn(content.status) === statusFilter) &&
    (!normalizedQuery || content.contentCode.toLowerCase().includes(normalizedQuery) ||
      content.title?.toLowerCase().includes(normalizedQuery) ||
      content.tasks.some((task) => task.taskName.toLowerCase().includes(normalizedQuery))));
  const counts = Object.fromEntries(CONTENT_COLUMNS.map((column) => [column.key,
    contents.filter((content) => contentColumn(content.status) === column.key).length]));

  return <section className="package-content-list">
    <div className="content-index-panel">
      <header><div><span className="panel-number">CONTENT LIST</span>
        <h2>内容清单</h2>
        <p>{canManage ? '服务包页只展示进度摘要；分工、修改、审核与交付统一在内容中心处理。' :
          '展示服务包全部内容；金额与收款信息按本人权限隐藏，进入内容中心可处理本人分工。'}</p></div>
        <strong>{contents.length}<small>{COPY[contentType].unit}</small></strong></header>
      <div className="content-index-stats">{CONTENT_COLUMNS.map((column) => <button key={column.key}
        className={statusFilter === column.key ? 'is-active' : ''} onClick={() => setStatusFilter((value) => value === column.key ? 'ALL' : column.key)}>
        <span>{column.label === '待确认' ? '待登记' : column.label}</span><b>{counts[column.key] ?? 0}</b></button>)}</div>
      <Input allowClear value={query} onChange={(event) => setQuery(event.target.value)}
        placeholder={`搜索${contentType === 'VIDEO' ? '视频' : '文章'}标题、编号或任务`} />
      <div className="content-index-list">
        {visible.length === 0 && <div className="content-index-empty">没有符合条件的内容</div>}
        {visible.map((content) => {
          const total = content.tasks.length;
          const progressed = content.status === 'DELIVERED' ? total :
            content.tasks.filter((task) => ['ACCEPTED', 'IN_PROGRESS', 'COMPLETED'].includes(task.status)).length;
          return <div key={content.contentId} className="content-index-row">
            <Link data-content-id={content.contentId}
            className={content.contentId === returnedContentId ? 'is-returned' : undefined}
            to={`/projects/${projectId}/content?package=${packageId}&content=${content.contentId}`}>
            <span className={`content-list-mark is-${contentColumn(content.status).toLowerCase()}`}>{contentType === 'VIDEO' ? '视' : '文'}</span>
            <span className="content-list-copy"><strong>{contentTitle(content.displayNo, content.legacyNo, content.title ?? `待确定${contentType === 'VIDEO' ? '视频' : '文章'}主题`)}</strong>
              <small>{content.contentCode} · {content.tierCode ?? '未选档'} · {canManage ? `${total} 项分工` :
                `共 ${total} 项 · 我的 ${countMyTasks(content.tasks, personId ?? null)} 项`}</small>
              <i><em style={{ width: `${total ? Math.round(progressed / total * 100) : 0}%` }} /></i></span>
            <span className="content-list-end"><b>{contentStatusLabel(content.status)}</b>
              {content.issueStats.blocking > 0 ? <small className="is-blocking">阻断 {content.issueStats.blocking}</small> :
                content.issueStats.mineOpen > 0 ? <small>我的问题 {content.issueStats.mineOpen}</small> : <small>问题 {content.issueStats.open}</small>}</span>
          </Link>
          {canManage && <Link className="content-index-action"
            to={publicationPlanPath({ projectId, contentId: content.contentId, packageId,
              status: statusContext ?? undefined, q: qContext ?? undefined, from: 'package' })}>安排发布计划</Link>}
          </div>;
        })}
      </div>
      <footer><Link to={`/projects/${projectId}/content?package=${packageId}`}><Button>打开统一内容中心 <Icon name="arrowRight" size={14} /></Button></Link></footer>
    </div>
  </section>;
}

export function ContentCreateDrawer({ open, onClose, token, accountId, projectId, packageId, contentType,
  variants, executorOptions, taskOptions, packageOptions, onPackageChange }: { open: boolean; onClose: () => void; token: string;
  accountId: string | null; projectId: string; packageId: string; contentType: PackagePageContentType;
  variants: Array<{ code: string; name: string; count: number; used: number }>;
  executorOptions?: Array<{ personId: string; label: string }> | undefined;
  taskOptions?: Array<{ code: string; name: string }> | undefined;
  packageOptions?: Array<{ packageId: string; label: string }> | undefined;
  onPackageChange?: ((packageId: string) => void) | undefined }) {
  const client = useQueryClient();
  const [form] = Form.useForm<{ variantKey: string; title: string; displayNo: number }>();
  const [taskAssignees, setTaskAssignees] = useState<Record<string, string | undefined>>({});
  const [displayNoEdited, setDisplayNoEdited] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const mutation = useMutation({ mutationFn: createContents });
  const capacity = useQuery({ queryKey: ['content-capacity', accountId, projectId, packageId],
    queryFn: () => getContentCapacity(token, projectId, packageId), enabled: open && !!token });
  const selectedVariant = Form.useWatch('variantKey', form);
  const remaining = capacity.data?.variants.find((row) => row.variantKey === selectedVariant)?.remaining ?? 0;

  useEffect(() => {
    if (open && capacity.data && !displayNoEdited)
      form.setFieldValue('displayNo', capacity.data.nextDisplayNo);
  }, [capacity.data, displayNoEdited, form, open]);

  async function create(values: { variantKey: string; title: string; displayNo: number }) {
    const title = values.title.trim();
    if (!title || remaining < 1 || !Number.isSafeInteger(values.displayNo) ||
      values.displayNo < 1 || values.displayNo > 999_999_999) return;
    setMessage(null);
    try {
      const assignments = buildContentAssignments(taskOptions ?? [], taskAssignees);
      const result = await mutation.mutateAsync({ token, projectId, packageId, key: `content-create-${crypto.randomUUID()}`,
        items: [{ title, variantKey: values.variantKey, assignments,
          ...(displayNoEdited ? { displayNo: values.displayNo } : {}) }] });
      const created = result.items[0];
      setMessage({ type: 'success', text: `已创建${contentType === 'VIDEO' ? '视频' : '文章'} ${created?.displayNo ?? created?.contentCode ?? ''}，剩余容量 ${result.capacity.remaining}。` });
      setDisplayNoEdited(false);
      form.setFieldsValue({ title: '', displayNo: result.capacity.nextDisplayNo }); setTaskAssignees({});
      await Promise.all([client.invalidateQueries({ queryKey: ['video-contents', contentType, accountId, projectId, packageId] }),
        client.invalidateQueries({ queryKey: ['video-packages', contentType, accountId, projectId] }),
        client.invalidateQueries({ queryKey: ['content-capacity', accountId, projectId, packageId] })]);
    } catch (failure) { setMessage({ type: 'error', text: failureMessage(failure) }); }
  }

  return <Drawer open={open} onClose={onClose} size={560} title={<div><span className="panel-number">CREATE CONTENT</span>
    <h2>创建真实{contentType === 'VIDEO' ? '视频' : '文章'}内容</h2></div>} destroyOnHidden>
    <p className="package-muted">创建会实时占用所选规格容量，重试不会重复新增。</p>
    {capacity.data && <div className="content-capacity-grid"><div><span>批准容量</span><strong>{capacity.data.capacity}</strong></div>
      <div><span>已创建</span><strong>{capacity.data.used}</strong></div><div><span>剩余</span><strong>{capacity.data.remaining}</strong></div></div>}
    {message && <Alert type={message.type} showIcon title={message.text} closable onClose={() => setMessage(null)} />}
    <Form form={form} layout="vertical" onFinish={(values) => void create(values)}
      initialValues={{ variantKey: variants.find((row) => row.used < row.count)?.code }}>
      {packageOptions && onPackageChange && <Form.Item label="项目包" required>
        <Select value={packageId} onChange={(value) => { setDisplayNoEdited(false); onPackageChange(value); }} options={packageOptions.map((row) => ({
          value: row.packageId, label: row.label,
        }))} />
      </Form.Item>}
      <Form.Item name="variantKey" label="服务规格" rules={[{ required: true }]}><Select options={variants.map((row) => ({
        value: row.code, disabled: row.used >= row.count, label: `${row.name || row.code} · 剩余 ${Math.max(0, row.count - row.used)}` }))} /></Form.Item>
      <Form.Item name="displayNo" label="内容序号" extra="系统已推荐下一个序号；需要时可直接修改，同一服务包内不能重复。"
        rules={[{ required: true, type: 'number', min: 1, max: 999_999_999 }]}>
        <InputNumber min={1} max={999_999_999} precision={0} controls={false} inputMode="numeric"
          style={{ width: '100%' }} onChange={() => { setDisplayNoEdited(true); setMessage(null); }} />
      </Form.Item>
      <Form.Item name="title" label="内容标题" rules={[{ required: true, min: 1, max: 160 }]}>
        <Input maxLength={160} showCount placeholder="2026 秋季健康选题" /></Form.Item>
      <div className="package-note">内容序号供日常使用；系统仍会自动生成不可修改的 VID/ART 代码，历史编号与标题保持原样。</div>
      <div className="package-note">可选：创建时可一次为全部制作任务选择负责人，未选项仍可在内容详情中补齐。</div>
      <div className="content-create-assignment-grid">{taskOptions?.map((task) => <label key={task.code}>
        <span><b>{task.name}</b><small>{task.code}</small></span>
        <Select allowClear showSearch optionFilterProp="label" placeholder="选择负责人"
          value={taskAssignees[task.code]} onChange={(personId) => setTaskAssignees((current) => ({
            ...current, [task.code]: personId,
          }))} options={executorOptions?.map((row) => ({ value: row.personId, label: row.label })) ?? []} />
      </label>)}</div>
      <Button block type="primary" htmlType="submit" loading={mutation.isPending} disabled={!selectedVariant || remaining < 1}>
        创建内容</Button>
    </Form>
  </Drawer>;
}

const PROJECT_DUTY_LABEL = {
  PROJECT_MANAGER: '项目负责人', PACKAGE_MANAGER: '服务包负责人', START_CONFIRMER: '启动确认人',
  DELIVERY_CONFIRMER: '交付确认人', TASK_EXECUTOR: '任务执行人', CONTENT_PLANNER: '内容策划',
  VIDEO_EDITOR: '视频剪辑', VIDEO_PRODUCTION: '视频制作', FINANCE_VIEWER: '财务查看',
  PUBLICATION_OPERATOR: '发布专员',
} as const;
const PROJECT_DUTY_OPTIONS = (Object.entries(PROJECT_DUTY_LABEL) as Array<[ProjectDuty, string]>).map(([value, label]) =>
  ({ value, label }));
// 内容策划会驱动当前项目的策划工作区与操作能力；仅隐藏已经停用的视频执行别名，历史数据仍可编辑移除。
const HIDDEN_LEGACY_EXECUTION_DUTIES = new Set<ProjectDuty>(['VIDEO_EDITOR', 'VIDEO_PRODUCTION']);

export function newMemberDutyOptions() {
  return PROJECT_DUTY_OPTIONS.filter((option) =>
    option.value !== 'PROJECT_MANAGER' && !HIDDEN_LEGACY_EXECUTION_DUTIES.has(option.value));
}

/**
 * BUG-0162：选择「内容策划」职责时，候选必须已具备全局 PLANNER 基线角色
 * （或本人是本项目项目负责人）。不满足的候选直接禁选，避免成员换了职责却没落地。
 */
export function plannerDutyBlocked(duties: readonly ProjectDuty[],
  candidate: { plannerEligible?: boolean } | undefined): boolean {
  return duties.includes('CONTENT_PLANNER') && candidate?.plannerEligible === false;
}

export function memberOptionDisabled(duties: readonly ProjectDuty[],
  candidate: { plannerEligible?: boolean } | undefined): boolean {
  return plannerDutyBlocked(duties, candidate);
}

export function existingMemberDutyOptions(memberDuties: ProjectDuty[]) {
  return PROJECT_DUTY_OPTIONS.filter((option) =>
    !HIDDEN_LEGACY_EXECUTION_DUTIES.has(option.value) || memberDuties.includes(option.value));
}

function teamErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      FORBIDDEN: '当前账号没有本项目团队管理权限，或关系已撤销。',
      PROJECT_NOT_FOUND: '项目不存在或已超出当前访问范围。',
      MEMBER_INVALID: '该人员账号或可分配状态已失效，请刷新候选列表。',
      MEMBER_EXISTS: '该人员已在项目团队中。',
      MEMBER_HAS_ACTIVE_WORK: '该成员仍有未完成任务或待处理问题，请先完成或改派后再移出。',
      VERSION_CONFLICT: '团队信息已被其他人更新，请刷新后重试。',
      STATE_CONFLICT: '项目已暂停/归档，或所选职责（如交付确认人）已被其他成员占用，请调整后重试。',
      PLANNER_ROLE_REQUIRED: '该成员还没有全局「内容策划」角色，请先在账号角色中补齐后再添加内容策划职责。',
      IDEMPOTENCY_PAYLOAD_CONFLICT: '本次操作内容发生变化，请重新发起。',
    };
    return messages[error.code] ?? error.message;
  }
  return error instanceof Error ? error.message : '操作失败，请刷新后重试。';
}

export function ProjectTeamDrawer({ open, onClose, token, accountId, projectId }: { open: boolean; onClose: () => void;
  token: string; accountId: string | null; projectId: string }) {
  const client = useQueryClient();
  const [personId, setPersonId] = useState<string>();
  const [duties, setDuties] = useState<ProjectDuty[]>(['TASK_EXECUTOR']);
  const [editing, setEditing] = useState<string | null>(null);
  const [editingDuties, setEditingDuties] = useState<ProjectDuty[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const members = useQuery({ queryKey: ['project-members', accountId, projectId],
    queryFn: () => listProjectMembers(token, projectId), enabled: open && !!token && !!accountId });
  const options = useQuery({ queryKey: ['project-member-options', accountId, projectId],
    queryFn: () => listProjectMemberOptions(token, projectId), enabled: open && !!token && !!accountId });
  const addMutation = useMutation({ mutationFn: addProjectMember });
  const updateMutation = useMutation({ mutationFn: updateProjectMember });
  const endMutation = useMutation({ mutationFn: endProjectMember });

  async function refresh() {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['project-members', accountId, projectId] }),
      client.invalidateQueries({ queryKey: ['project-member-options', accountId, projectId] }),
      client.invalidateQueries({ queryKey: ['video-executors'] }),
    ]);
  }

  async function add() {
    if (!personId || !duties.length) return;
    setError(null); setNotice(null);
    try {
      await addMutation.mutateAsync({ token, projectId, personId, duties,
        idempotencyKey: `member-add-${crypto.randomUUID()}` });
      const label = options.data?.items.find((item) => item.personId === personId)?.displayName ?? '成员';
      setPersonId(undefined); setDuties(['TASK_EXECUTOR']); setNotice(`${label} 已加入项目团队。`);
      await refresh();
    } catch (failure) { setError(teamErrorMessage(failure)); }
  }

  function beginEdit(member: ProjectMember) {
    setEditing(member.membershipId); setEditingDuties(member.duties); setError(null); setNotice(null);
  }

  async function saveMember(member: ProjectMember) {
    if (!editingDuties.length) return;
    setError(null); setNotice(null);
    try {
      await updateMutation.mutateAsync({ token, projectId, membershipId: member.membershipId,
        expectedVersion: member.version, duties: editingDuties,
        idempotencyKey: `member-update-${crypto.randomUUID()}` });
      setEditing(null); setEditingDuties([]); setNotice(`${member.displayName} 的项目职责已更新。`); await refresh();
    } catch (failure) { setError(teamErrorMessage(failure)); }
  }

  async function end(membershipId: string, expectedVersion: number, displayName: string) {
    setError(null); setNotice(null);
    try {
      await endMutation.mutateAsync({ token, projectId, membershipId, expectedVersion,
        idempotencyKey: `member-end-${crypto.randomUUID()}` });
      setNotice(`${displayName} 已移出项目团队。`);
      await refresh();
    } catch (failure) { setError(teamErrorMessage(failure)); }
  }

  return <Drawer rootClassName="project-team-drawer" size={600} open={open} onClose={onClose}
    title={<div><span className="panel-number">PROJECT TEAM</span><h2>项目团队与任务权限</h2></div>}>
    <p className="package-muted">一个人在同一项目可以承担多个职责；只有具备“任务执行人”职责且当前可分配的人，才会进入任务分派候选。</p>
    {error && <Alert type="error" showIcon title={error} closable onClose={() => setError(null)} />}
    {notice && <Alert type="success" showIcon title={notice} closable onClose={() => setNotice(null)} />}
    {(() => {
      const selectedCandidate = options.data?.items.find((item) => item.personId === personId);
      const plannerBlocked = plannerDutyBlocked(duties, selectedCandidate);
      return <div className="project-team-add"><Select showSearch optionFilterProp="label" value={personId}
        onChange={setPersonId} loading={options.isFetching} placeholder="选择执行人或项目经理"
        options={options.data?.items.map((item) => ({ value: item.personId,
          label: `${item.displayName} · ${item.personCode}${item.plannerEligible ? '' : ' · 无内容策划角色'}`,
          disabled: memberOptionDisabled(duties, item) })) ?? []} />
      <Select mode="multiple" value={duties} onChange={(value) => setDuties(value as ProjectDuty[])}
        options={newMemberDutyOptions()}
        placeholder="选择项目职责" />
      <Button type="primary" disabled={!personId || !duties.length || plannerBlocked}
        loading={addMutation.isPending} onClick={() => void add()}>加入团队</Button>
      {plannerBlocked && <small className="field-hint">该成员还没有全局「内容策划」角色，请先在账号角色中补齐后再选「内容策划」职责。</small>}
    </div>;
    })()}
    {(members.isPending || options.isPending) && <Skeleton active paragraph={{ rows: 4 }} />}
    {(members.isError || options.isError) && <Alert type="error" showIcon title="项目团队读取失败"
      description={teamErrorMessage(members.error ?? options.error)} />}
    <div className="project-team-list">{members.data?.items.map((member) => <article key={member.membershipId}>
      <div><strong>{member.displayName}</strong><small>{member.personCode} · {member.department ?? '部门待完善'} · {member.activeTaskCount} 个进行中任务</small></div>
      {editing === member.membershipId ? <div className="project-team-duty-editor"><Select mode="multiple" value={editingDuties}
        options={existingMemberDutyOptions(member.duties)
          .map((option) => ({ ...option,
          disabled: option.value === 'PROJECT_MANAGER' && member.duties.includes('PROJECT_MANAGER') }))}
        onChange={(value) => setEditingDuties(member.duties.includes('PROJECT_MANAGER') ?
          [...new Set(['PROJECT_MANAGER', ...value])] as ProjectDuty[] : value as ProjectDuty[])} />
        <Button size="small" type="primary" loading={updateMutation.isPending} onClick={() => void saveMember(member)}>保存</Button>
        <Button size="small" onClick={() => setEditing(null)}>取消</Button></div> : <div className="project-team-duty-tags">
        {member.duties.map((duty) => <Tag key={duty}
          color={duty === 'PROJECT_MANAGER' ? 'blue' : duty === 'DELIVERY_CONFIRMER' ? 'gold' : 'default'}>
          {PROJECT_DUTY_LABEL[duty]}</Tag>)}</div>}
      {editing !== member.membershipId && <Button size="small" onClick={() => beginEdit(member)}>调整职责</Button>}
      {!member.duties.includes('PROJECT_MANAGER') ? <Popconfirm title={`移出 ${member.displayName}？`}
        description="若该成员仍有未完成任务或待处理问题，系统会拒绝移出。" okText="确认移出" cancelText="取消"
        onConfirm={() => end(member.membershipId, member.version, member.displayName)}>
        <Button danger size="small" loading={endMutation.isPending}>移出</Button></Popconfirm> : <span className="project-team-locked">固定角色</span>}
    </article>)}</div>
  </Drawer>;
}

export function VideoPackagesPage() {
  return <PackagePage contentType="VIDEO" />;
}

export function ArticlePackagesPage() {
  return <PackagePage contentType="ARTICLE" />;
}

export function ServicePackagesPage() {
  const [searchParams] = useSearchParams();
  return <PackagePage contentType={searchParams.get('type') === 'ARTICLE' ? 'ARTICLE' : 'VIDEO'} />;
}

export function packageTypeSwitchPath(projectId: string, next: PackagePageContentType, params: URLSearchParams): string {
  const search = new URLSearchParams(params);
  if (next === 'ARTICLE') search.set('type', 'ARTICLE');
  else search.delete('type');
  const query = search.toString();
  return `/projects/${projectId}/packages${query ? `?${query}` : ''}`;
}

function PackagePage({ contentType }: { contentType: PackagePageContentType }) {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useModuleViewMode(searchParams, setSearchParams);
  const navigate = useNavigate();
  function switchPackageType(next: PackagePageContentType) {
    if (!projectId || next === contentType) return;
    navigate(packageTypeSwitchPath(projectId, next, searchParams));
  }
  const packageTypeSwitch = (
    <Segmented className="package-type-switch" size="small" value={contentType}
      options={[{ label: '视频', value: 'VIDEO' }, { label: '文章', value: 'ARTICLE' }]}
      onChange={(value) => switchPackageType(value as PackagePageContentType)} />
  );
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : null;
  const personId = state.kind === 'ready' ? state.session.personId : null;
  const [form] = Form.useForm<FormValues>();
  const [definitionForm] = Form.useForm<DefinitionFormValues>();
  const [actionForm] = Form.useForm<{ reason: string; completedOn: string; note: string }>();
  const on = Form.useWatch('businessOn', form);
  const templateId = Form.useWatch('serviceTemplateVersionId', form);
  const variantCounts = Form.useWatch('variants', form) ?? [];
  const flexibleReserve = Form.useWatch('flexibleReserve', form) ?? '0';
  const definitionVariantCounts = Form.useWatch('variants', definitionForm) ?? [];
  const definitionFlexibleReserve = Form.useWatch('flexibleReserve', definitionForm) ?? '0';
  const definitionReason = Form.useWatch('reason', definitionForm) ?? '';
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(searchParams.get('create') === '1');
  const [editingPackage,setEditingPackage]=useState<VideoPackageSummary|null>(null);
  const [editingDefinitionPackage,setEditingDefinitionPackage]=useState<VideoPackageSummary|null>(null);
  const [definitionOpen,setDefinitionOpen]=useState(false);
  const [actionDialog,setActionDialog]=useState<'return'|'complete'|'reopen'|null>(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [contentCreateOpen, setContentCreateOpen] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(searchParams.get('package'));
  // BUG-0168：从发布页回到服务包页时高亮刚才那条内容。
  const returnedContentId = searchParams.get('from') === 'publications' ? searchParams.get('content') : null;
  useEffect(() => {
    if (!returnedContentId) return;
    const scroll = window.setTimeout(() => {
      document.querySelector(`[data-content-id="${returnedContentId}"]`)?.scrollIntoView({ block: 'center' });
    }, 60);
    const clear = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('from');
      setSearchParams(next, { replace: true });
    }, 2600);
    return () => { window.clearTimeout(scroll); window.clearTimeout(clear); };
  }, [returnedContentId, searchParams, setSearchParams]);
  const [contentCursor, setContentCursor] = useState(0);
  const [tableFilter, setTableFilter] = useState<PackageStatusFilter>('ALL');
  const [tableSearch, setTableSearch] = useState('');
  const attempt = useRef<{ payload: string; key: string } | null>(null);
  const definitionAttempt = useRef<{ payload: string; key: string } | null>(null);
  const definitionSeed = useRef<string | null>(null);
  const client = useQueryClient();
  const project = useQuery({ queryKey: ['project', accountId, projectId],
    queryFn: () => getProject(token, projectId!), enabled: !!token && !!accountId && !!projectId });
  const accessProfile = useQuery({ queryKey: ['project-access-profile', accountId, projectId],
    queryFn: () => getProjectAccessProfile(token, projectId!), enabled: !!token && !!accountId && !!projectId });
  const access = resolveProjectWorkspaceAccess({
    roles: state.kind === 'ready' ? state.session.roles : [],
    capabilities: state.kind === 'ready' ? state.session.capabilities : [],
    membershipId: accessProfile.data?.membershipId ?? null,
    duties: accessProfile.data?.duties ?? [],
  });
  const pureExecutor = access.mode === 'EXECUTOR';
  const fullWorkspace = access.mode === 'FULL';
  const canDraft = state.kind === 'ready' && state.session.capabilities.includes('package.manage_draft') &&
    (access.administrator || access.projectManager);
  const canCreateContent = access.canCreateContent;
  const isAdmin = access.administrator;
  const team=useQuery({queryKey:['project-members',accountId,projectId],
    queryFn:()=>listProjectMembers(token,projectId!),enabled:!!token&&!!accountId&&!!projectId&&fullWorkspace&&
      state.kind==='ready'&&state.session.pageAccess.includes('PROJECT_MEMBERS')});
  const packages = useQuery({ queryKey: ['video-packages', contentType, accountId, projectId],
    queryFn: () => listVideoPackages(token, projectId!, contentType),
    enabled: !!token && !!accountId && !!projectId && accessProfile.isSuccess && fullWorkspace });
  const mine = useQuery({ queryKey: ['my-video-packages', contentType, accountId, projectId],
    queryFn: () => listMyVideoPackages(token, projectId!, contentType),
    enabled: !!token && !!accountId && !!projectId && accessProfile.isSuccess && pureExecutor });
  const selectedPackage = packages.data?.items.find((item) => item.packageId === selectedPackageId) ??
    packages.data?.items.find((item) => item.status === 'ACTIVE') ?? packages.data?.items[0];
  const packageClaims = useQuery({
    queryKey: ['package-claims-detail', accountId, projectId, selectedPackage?.packageId],
    queryFn: () => listOperationalClaims(token, projectId!, selectedPackage!.packageId),
    enabled: !!token && !!projectId && !!selectedPackage && canDraft,
  });
  const activePackageId = pureExecutor ?
    (mine.data?.items.find((item) => item.packageId === selectedPackageId) ?? mine.data?.items[0])?.packageId :
    selectedPackage?.status === 'ACTIVE' ? selectedPackage.packageId : null;
  const contents = useQuery({ queryKey: ['video-contents', contentType, accountId, projectId, activePackageId, contentCursor],
    queryFn: () => listVideoContents(token, projectId!, activePackageId!, contentCursor, contentType),
    enabled: !!token && !!projectId && !!activePackageId });
  const executors = useQuery({ queryKey: ['video-executors', contentType, accountId, projectId, selectedPackage?.packageId],
    queryFn: () => videoTaskExecutors(token, projectId!, selectedPackage!.packageId, contentType),
    enabled: !!token && !!projectId && selectedPackage?.status === 'ACTIVE' && access.canAssignTasks });
  const options = useQuery({ queryKey: ['video-package-options', contentType, accountId, projectId, on],
    queryFn: () => videoPackageOptions(token, projectId!, on!, contentType),
    enabled: !!token && !!accountId && !!projectId && !!on && canDraft && /^\d{4}-\d{2}-\d{2}$/.test(on) });
  const definitionOptions = useQuery({ queryKey: ['video-package-definition-options', accountId, projectId,
    editingDefinitionPackage?.packageId], queryFn: () => videoPackageDefinitionOptions({ token, projectId: projectId!,
      packageId: editingDefinitionPackage!.packageId }),
    enabled: definitionOpen && !!token && !!accountId && !!projectId && !!editingDefinitionPackage });
  const draftMutation = useMutation({ mutationFn: draftVideoPackage });
  const updateDraftMutation=useMutation({mutationFn:updateVideoPackageDraft});
  const definitionMutation=useMutation({mutationFn:updateVideoPackageDefinition});
  const lifecycleMutation=useMutation({mutationFn:async(input:{action:'submit'|'confirm'|'start';item:VideoPackageSummary})=>{
    if(!projectId)throw new Error('Invalid project');
    const base={token,projectId,contentType,packageId:input.item.packageId,
      expectedVersion:input.item.version,key:`package-${input.action}-${crypto.randomUUID()}`};
    if(input.action==='submit')return submitVideoPackage(base);
    if(input.action==='confirm')return confirmVideoPackage(base);
    return startVideoPackage(base);
  }});
  const template = options.data?.templates.find((row) => row.versionId === templateId);
  const completion=useQuery({queryKey:['package-completion',contentType,accountId,projectId,selectedPackage?.packageId,
    selectedPackage?.version],queryFn:()=>previewVideoPackageCompletion(token,projectId!,selectedPackage!.packageId,contentType),
    enabled:!!token&&!!projectId&&selectedPackage?.status==='ACTIVE'&&canDraft});
  const estimatedProduction=template?.variants.reduce((sum,variant)=>sum+Number(variant.productionBudget)*
    (variantCounts.find((row)=>row.variantKey===variant.variantKey)?.count??0),0)??0;
  const estimatedPublication=template?.variants.reduce((sum,variant)=>sum+Number(variant.publicationBudget)*
    (variantCounts.find((row)=>row.variantKey===variant.variantKey)?.count??0),0)??0;
  const definitionTemplate = definitionOptions.data?.templates[0];
  const definitionPreview = calculatePackageDefinitionPreview(definitionTemplate?.variants ?? [],
    definitionVariantCounts, definitionFlexibleReserve);
  const definitionBelowUsed = isPackageDefinitionBelowUsed(definitionVariantCounts,
    editingDefinitionPackage?.tiers ?? []);
  const definitionUsed = editingDefinitionPackage?.tiers.reduce((sum, tier) => sum + tier.used, 0) ?? 0;
  const definitionBudgetDelta = definitionPreview.total - Number(editingDefinitionPackage?.budget ?? 0);
  const definitionCanSave = definitionPreview.capacity > 0 && !definitionBelowUsed &&
    definitionReason.trim().length >= 2 && !definitionMutation.isPending;

  useEffect(() => {
    const item = editingDefinitionPackage;
    const templateOptions = definitionOptions.data?.templates[0];
    const seed = item ? `${item.packageId}:${item.version}` : null;
    if (!definitionOpen || !item || !templateOptions || definitionSeed.current === seed) return;
    const current = new Map(item.tiers.map((tier) => [tier.code, tier.count]));
    definitionForm.setFieldsValue({ packageTitle: item.packageTitle, businessOn: item.businessOn,
      periodFrom: item.periodFrom, periodUntil: item.periodUntil,
      variants: templateOptions.variants.map((variant) => ({ variantKey: variant.variantKey,
        count: current.get(variant.variantKey) ?? 0 })), flexibleReserve: item.flexibleReserve,
      managerId: item.managerId, confirmerId: item.confirmerId, reason: '' });
    definitionSeed.current = seed;
  }, [definitionForm, definitionOpen, definitionOptions.data, editingDefinitionPackage]);

  async function create(values: FormValues) {
    if (!projectId) return;
    const draft: VideoPackageDraftRequest = { ...values, expectedVersion: 0,
      packageTitle: values.packageTitle?.trim() || null,
      flexibleReserve: normalizeMoneyInput(values.flexibleReserve),
      variants: values.variants.map((variant) => ({ variantKey: variant.variantKey, count: variant.count })) };
    const payload = JSON.stringify(draft);
    if (attempt.current?.payload !== payload) attempt.current = { payload, key: `package-${crypto.randomUUID()}` };
    setError(null); setSuccess(null);
    try {
      const result=editingPackage?await updateDraftMutation.mutateAsync({token,projectId,contentType,
        packageId:editingPackage.packageId,key:attempt.current.key,draft:{...draft,expectedVersion:editingPackage.version}}):
        await draftMutation.mutateAsync({ token, projectId, contentType, key: attempt.current.key, draft });
      setSuccess(`${result.packageCode} 草稿已保存。提交确认时系统会再次核算全部费用。`);
      attempt.current = null; form.resetFields(); setCreateOpen(false);
      setEditingPackage(null);setSelectedPackageId(result.packageId);
      await client.invalidateQueries({ queryKey: ['video-packages', contentType, accountId, projectId] });
    } catch (error) { const message = failureMessage(error); setError(message); notifyActionFailure(message); }
  }

  function editDraft(item:VideoPackageSummary){
    setEditingPackage(item);attempt.current=null;setCreateOpen(true);
    form.setFieldsValue({serviceTemplateVersionId:item.serviceTemplateVersionId,packageTitle:item.packageTitle,businessOn:item.businessOn,
      periodFrom:item.periodFrom,periodUntil:item.periodUntil,capacity:item.capacity,
      variants:item.tiers.map((tier)=>({variantKey:tier.code,count:tier.count})),
      flexibleReserve:item.flexibleReserve,managerId:item.managerId,confirmerId:item.confirmerId});
  }

  function editDefinition(item: VideoPackageSummary) {
    setEditingDefinitionPackage(item); definitionAttempt.current = null; definitionSeed.current = null;
    setError(null); definitionForm.resetFields(); setDefinitionOpen(true);
  }

  function closeDefinitionEditor() {
    setDefinitionOpen(false); setEditingDefinitionPackage(null); definitionSeed.current = null;
    definitionAttempt.current = null; definitionForm.resetFields();
  }

  function changeDefinitionCount(index: number, delta: number, minimum: number) {
    definitionForm.setFieldValue('variants', adjustPackageDefinitionCount(
      definitionForm.getFieldValue('variants') ?? [], index, delta, minimum));
    void definitionForm.validateFields([['variants', index, 'count']]);
  }

  async function saveDefinition(values: DefinitionFormValues) {
    if (!projectId || !editingDefinitionPackage || !definitionTemplate) return;
    const variants = normalizePackageDefinitionVariants(definitionTemplate.variants, values.variants);
    const capacity = variants.reduce((sum, row) => sum + row.count, 0);
    const change: VideoPackageDefinitionUpdateRequest = { ...values,
      expectedVersion: editingDefinitionPackage.version, capacity,
      packageTitle: values.packageTitle?.trim() || null,
      flexibleReserve: normalizeMoneyInput(values.flexibleReserve),
      variants };
    const payload = JSON.stringify(change);
    if (definitionAttempt.current?.payload !== payload) {
      definitionAttempt.current = { payload, key: `package-definition-${crypto.randomUUID()}` };
    }
    setError(null); setSuccess(null);
    try {
      const result = await withTimeout((signal) => definitionMutation.mutateAsync({ token, projectId,
        packageId: editingDefinitionPackage.packageId, key: definitionAttempt.current!.key, change, signal }));
      setSuccess(`${result.packageCode} 的基础信息和服务条数已更新，现有内容与任务记录未改动。`);
      definitionAttempt.current = null; definitionSeed.current = null; definitionForm.resetFields();
      closeDefinitionEditor(); setSelectedPackageId(result.packageId);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['video-packages', contentType, accountId, projectId] }),
        client.invalidateQueries({ queryKey: ['package-completion', contentType, accountId, projectId, result.packageId] }),
      ]);
    } catch (failure) { const message = failureMessage(failure); setError(message); notifyActionFailure(message); }
  }

  async function transition(item: VideoPackageSummary,action:'submit'|'confirm'|'start') {
    if (!projectId) return;
    setError(null); setSuccess(null);
    try {
      const result=await lifecycleMutation.mutateAsync({action,item});
      const verb={submit:'已提交确认',confirm:'已确认，等待负责人正式启动',start:'已启动；内容将在实际制作时按需创建'}[action];
      setSuccess(`${result.packageCode} ${verb}。`);
      await client.invalidateQueries({ queryKey: ['video-packages', contentType, accountId, projectId] });
    } catch (error) { setError(failureMessage(error)); }
  }

  async function submitAction(values:{reason?:string;completedOn?:string;note?:string}){
    if(!projectId||!selectedPackage||!actionDialog)return;
    setError(null);
    try{
      const common={token,projectId,contentType,packageId:selectedPackage.packageId,
        expectedVersion:selectedPackage.version,key:`package-${actionDialog}-${crypto.randomUUID()}`};
      const result=actionDialog==='return'?await returnVideoPackage({...common,reason:values.reason??''}):
        actionDialog==='reopen'?await reopenVideoPackage({...common,reason:values.reason??''}):
          await completeVideoPackage({...common,completedOn:values.completedOn??'',note:values.note??''});
      setSuccess(`${result.packageCode} ${actionDialog==='return'?'已退回修改':actionDialog==='reopen'?'已撤销完成并恢复执行':'已完成登记'}。`);
      setActionDialog(null);actionForm.resetFields();
      await Promise.all([client.invalidateQueries({queryKey:['video-packages',contentType,accountId,projectId]}),
        client.invalidateQueries({queryKey:['package-completion',contentType,accountId,projectId,selectedPackage.packageId]})]);
    }catch(failure){const message=failureMessage(failure);setError(message);notifyActionFailure(message);}
  }

  // 管理层与纯执行者共用同一份表格行数据；执行者缺失的容量/预算/负责人按权限降级为“—”。
  const managerPackageRows = buildPackageTableRows((packages.data?.items ?? []).map((item) => ({
    packageId: item.packageId, packageCode: item.packageCode, status: item.status,
    templateName: item.templateName, packageTitle: item.packageTitle, contentType: item.contentType,
    periodFrom: item.periodFrom, periodUntil: item.periodUntil, capacity: item.capacity,
    used: item.tiers.reduce((sum, tier) => sum + tier.used, 0), budget: item.budget,
    amountsHidden: item.amountsHidden,
    owner: team.data?.items.find((member) => member.personId === item.managerId)?.displayName ??
      (project.data?.primaryManager.id === item.managerId ? project.data.primaryManager.displayName : undefined),
  })));
  // BUG-0172（P0）：执行人包投影只有标识字段，模板名与周期不再下发。
  const executorPackageRows = buildPackageTableRows((mine.data?.items ?? []).map((item) => ({
    packageId: item.packageId, packageCode: item.packageCode, status: item.status,
    templateName: item.packageCode, packageTitle: item.packageTitle, contentType,
    periodFrom: '', periodUntil: '',
  })));
  const packageRows = pureExecutor ? executorPackageRows : managerPackageRows;
  const visiblePackageRows = filterPackageTableRows(packageRows, tableFilter, tableSearch);
  const searchedPackageRows = filterPackageTableRows(packageRows, 'ALL', tableSearch);
  const packageTableFilters = PACKAGE_STATUS_FILTERS.map((option) => ({ ...option,
    count: packageFilterCount(searchedPackageRows, option.value) }));
  const packageSearchPlaceholder = `搜索${contentType === 'VIDEO' ? '视频' : '文章'}服务包名称、编号或负责人`;
  const requestedPackageId = searchParams.get('package');
  const tableDetail = requestedPackageId ? packageRows.find((row) => row.packageId === requestedPackageId) ?? null : null;
  // 表格抽屉渲染被点击的那一个服务包，而不是分栏的兜底选中项。
  const tableManagerPackage = tableDetail ?
    packages.data?.items.find((item) => item.packageId === tableDetail.packageId) ?? null : null;
  const dataTaskCount = (contents.data?.items ?? []).reduce((sum, entry) =>
    sum + entry.tasks.filter((task) => task.assignees.some((row) => row.personId === personId) ||
      task.executorId === personId).length, 0);
  const packageStats: ModuleStat[] = pureExecutor ? [
    { label: '服务包', value: String(executorPackageRows.length) },
    { label: '我的任务', value: String(dataTaskCount) },
    { label: '本页内容', value: String(contents.data?.items.length ?? 0) },
  ] : [
    { label: '服务包', value: String(managerPackageRows.length) },
    { label: '执行中', value: String(packageFilterCount(managerPackageRows, 'ACTIVE')) },
    { label: '已完成', value: String(packageFilterCount(managerPackageRows, 'COMPLETED')) },
    { label: '总容量', value: String((packages.data?.items ?? []).reduce((sum, item) => sum + item.capacity, 0)),
      tone: 'dark' },
  ];

  function changePackageView(mode: ModuleViewMode) {
    setViewMode(mode);
  }

  /** 表格行打开时同步分栏选中，抽屉直接复用分栏右侧的完整详情与操作。 */
  function openPackageDetail(row: PackageTableRow) {
    setSelectedPackageId(row.packageId); setContentCursor(0);
    const next = new URLSearchParams(searchParams); next.set('package', row.packageId); setSearchParams(next, { replace: true });
  }

  function closePackageDetail() {
    const next = new URLSearchParams(searchParams); next.delete('package'); setSearchParams(next, { replace: true });
  }

  /** BUG-0176：服务端掩码时显示「无查看权限」，不要把裁剪后的 0.00 当成真实金额。 */
  const moneyText = (value: string, hidden: boolean | undefined) => hidden ? '无查看权限' : `¥${value}`;
  const unitPriceText = (value: string, hidden: boolean | undefined) => hidden ? '无查看权限'
    : `${formatPackageAmount(value)} / 条`;

  function renderManagerPackageDetail(selected: VideoPackageSummary) {
    const owns = selected.managerId === personId;
    const confirms = selected.confirmerId === personId;
    const usedCapacity = selected.tiers.reduce((sum, tier) => sum + tier.used, 0);
    return <>
      <header className="delivery-stage-banner"><div><span className="panel-number">{selected.packageCode}</span>
        <h2>{selected.packageTitle ?? selected.templateName} · {PACKAGE_STATUS_LABEL[selected.status]}</h2>
        <p>{selected.periodFrom} — {selected.periodUntil} · {selected.tiers.map((tier) => `${tier.name} ${tier.used}/${tier.count}`).join(' · ')}</p>
        {selected.legacyTemplate && <p className="package-legacy-template">历史模板来源：{selected.legacyTemplate.familyName ?? '—'} · {selected.legacyTemplate.versionName ?? ''}</p>}</div>
        <div><small>约定交付</small><strong>{selected.capacity}</strong></div>
        <div><small>模板版本</small><strong>{selected.templateVersionName}</strong></div>
        {selected.status === 'ACTIVE' && canDraft && <Link to={`/projects/${projectId!}/operations`}>
          <Button>项目费用记录</Button></Link>}
      </header>
      <div className="package-lifecycle-strip">{['草稿','待确认','已确认','执行中','已完成'].map((label,index)=><div
        key={label} className={index<PACKAGE_STATUS_STEP[selected.status]?'is-done':index===PACKAGE_STATUS_STEP[selected.status]?'is-current':''}>
        <i>{index<PACKAGE_STATUS_STEP[selected.status]?<Icon name="check" size={13} />:index+1}</i><span>{label}</span></div>)}</div>
      <div className="package-budget-ledger">
        <div><small>{contentType === 'VIDEO' ? '视频固定费用' : '内容固定费用'}</small><strong>{moneyText(selected.fixedCommitment, selected.amountsHidden)}</strong></div>
        <div><small>其中·发布任务</small><strong>{moneyText(selected.publicationBudget, selected.amountsHidden)}</strong></div>
        <div><small>灵活预留</small><strong>{selected.amountsHidden ? '无查看权限'
          : Number(selected.flexibleReserve) ? `¥${selected.flexibleReserve}` : '未设置'}</strong></div>
        <div><small>服务包总预算</small><strong>{moneyText(selected.budget, selected.amountsHidden)}</strong></div>
      </div>
      <section className="package-detail-section package-service-breakdown">
        <header><div><span className="panel-number">SERVICE STANDARD</span><h3>服务内容、条数与费用标准</h3></div>
          <small>固定费用按约定条数 × 单条标准计算</small></header>
        <div className="package-service-table" role="table" aria-label="服务内容与费用标准">
          <div className="package-service-row is-heading" role="row">
            <span>服务内容</span><span>约定</span><span>已创建</span><span>已交付</span><span>单条费用标准</span><span>固定费用</span>
          </div>
          {selected.tiers.map((tier) => <Fragment key={tier.code}>
            <div className="package-service-row" role="row">
              <span><strong>{tier.name}</strong><small>{tier.description || tier.code}</small></span>
              <b>{tier.count} 条</b><b>{tier.created} 条</b><b>{tier.delivered} 条</b>
              <span><strong>{unitPriceText(tier.unitPrice, selected.amountsHidden)}</strong>
                <small>制作 {selected.amountsHidden ? '无查看权限' : formatPackageAmount(tier.productionUnitPrice)} · 发布 {selected.amountsHidden ? '无查看权限' : formatPackageAmount(tier.publicationUnitPrice)}</small></span>
              <strong>{selected.amountsHidden ? '无查看权限' : formatPackageAmount(packageTierLineBudget(tier))}</strong>
            </div>
            <details className="package-service-tasks"><summary>查看 {tier.name} 逐项费用标准（{tier.tasks.length} 项）</summary>
              <div>{tier.tasks.map((task) => <span key={task.code}><b>{task.name}</b>
                <small>{task.quantity} {task.unit} × {formatPackageAmount(task.rate)}</small>
                <strong>{formatPackageAmount(task.subtotal)}</strong></span>)}</div>
            </details>
          </Fragment>)}
        </div>
      </section>
      {canDraft && <section className="package-detail-section package-reserve-usage">
        <header><div><span className="panel-number">FLEXIBLE RESERVE</span><h3>灵活预留费支配情况</h3></div>
          <Link to={`/projects/${projectId!}/operations?package=${selected.packageId}`}>查看全部费用记录 <Icon name="arrowRight" size={14} /></Link></header>
        {packageClaims.isPending && <Skeleton active paragraph={{ rows: 2 }} />}
        {packageClaims.isError && <Alert type="error" title="灵活预留费读取失败"
          description={failureMessage(packageClaims.error)} action={<Button onClick={() => void packageClaims.refetch()}>重试</Button>} />}
        {packageClaims.data?.reserve && <div className="package-reserve-summary">
          <div><small>批准预留</small><strong>{Number(packageClaims.data.reserve.budget)
            ? formatPackageAmount(packageClaims.data.reserve.budget) : '未设置'}</strong></div>
          <div><small>已支配 / 已占用</small><strong>{formatPackageAmount(packageClaims.data.reserve.occupied)}</strong></div>
          <div className={Number(packageClaims.data.reserve.available) < 0 ? 'is-over' : ''}><small>当前可用</small>
            <strong>{formatPackageAmount(packageClaims.data.reserve.available)}</strong>
            {Number(packageClaims.data.reserve.available) < 0 && <small>已超出批准预留</small>}</div>
        </div>}
        {packageClaims.data && <div className="package-reserve-list">
          {packageClaims.data.items.length === 0 && <p>尚无工作或报销记录，灵活预留费尚未支配。</p>}
          {packageClaims.data.items.map((claim) => <article key={claim.claimId}>
            <span><Tag>{claim.kind === 'WORK' ? '工作' : '报销'}</Tag><b>{claim.title}</b>
              <small>{claim.code} · {claim.payeeName} · {claim.actualOn}</small></span>
            <span><strong>{formatPackageAmount(claim.amount)}</strong><small>{packageClaimStatusLabel(claim)}</small></span>
          </article>)}
        </div>}
      </section>}
      <div className="delivery-start-panel package-action-panel"><div><span className="panel-number">NEXT ACTION</span>
        <h3>{selected.status==='DRAFT'?'完善草稿并提交确认':selected.status==='RETURNED'?'按退回意见修改后重新提交':
          selected.status==='SUBMITTED'?'等待启动确认人处理':selected.status==='CONFIRMED'?'由服务包负责人正式启动':
            selected.status==='ACTIVE'?'执行与完成检查':`已于 ${selected.completedOn??'—'} 完成`}</h3>
        <p>{selected.status==='RETURNED'?selected.returnReason:selected.deliverable}</p></div>
        <Space wrap>
          {isAdmin&&['ACTIVE','COMPLETED'].includes(selected.status)&&
            <Button onClick={()=>editDefinition(selected)}>修改服务包</Button>}
          {['DRAFT','RETURNED'].includes(selected.status)&&canDraft&&<><Button onClick={()=>editDraft(selected)}>编辑草稿</Button>
            <Button type="primary" loading={lifecycleMutation.isPending} onClick={()=>void transition(selected,'submit')}>提交确认</Button></>}
          {selected.status==='SUBMITTED'&&confirms&&<><Button danger onClick={()=>{setActionDialog('return');actionForm.resetFields();}}>退回修改</Button>
            <Button type="primary" loading={lifecycleMutation.isPending} onClick={()=>void transition(selected,'confirm')}>确认服务包</Button></>}
          {selected.status==='CONFIRMED'&&owns&&<Button type="primary" loading={lifecycleMutation.isPending}
            onClick={()=>void transition(selected,'start')}>正式启动</Button>}
          {selected.status==='ACTIVE'&&owns&&<Button type="primary" disabled={!completion.data?.canComplete}
            loading={completion.isFetching} onClick={()=>{setActionDialog('complete');actionForm.setFieldsValue({
              completedOn:new Date().toISOString().slice(0,10),note:''});}}>完成服务包</Button>}
          {selected.status==='COMPLETED'&&canReopenPackage({isAdmin,owns})&&<Button
            onClick={()=>{setActionDialog('reopen');actionForm.resetFields();}}>撤销完成</Button>}
        </Space>
      </div>
      {selected.status==='ACTIVE'&&completion.data&&<div className="package-completion-check">
        <div><strong>{completion.data.canComplete?'已通过完成检查':'尚未满足完成条件'}</strong>
          <span>已使用 {completion.data.usedCapacity}/{completion.data.capacity}</span></div>
        {completion.data.blockers.map((blocker)=><Link key={blocker.code} to={blocker.href}>{blocker.label} · {blocker.count} 项 <Icon name="arrowRight" size={14} /></Link>)}
        {completion.data.warnings.map((warning)=><p key={warning.code}>{warning.label}（{warning.count}）</p>)}
      </div>}
      {selected.status === 'ACTIVE' && <>
        <div className="content-create-bar"><div><strong>按实际需要创建内容</strong>
          <span>批准 {selected.capacity} 条 · 已创建 {usedCapacity} · 剩余 {selected.capacity - usedCapacity}</span></div>
          {canCreateContent && <Button type="primary" onClick={() => setContentCreateOpen(true)}><Icon name="plus" size={16} strokeWidth={2} /> 创建内容</Button>}</div>
        {contents.isPending && <Skeleton active paragraph={{ rows: 6 }} />}
        {contents.isError && <Alert type="error" title="内容读取失败" description={failureMessage(contents.error)}
          action={<Button onClick={() => void contents.refetch()}>重试</Button>} />}
        <PackageContentList projectId={projectId!} packageId={selected.packageId} returnedContentId={returnedContentId}
          statusContext={searchParams.get('status')} qContext={searchParams.get('q')}
          contents={contents.data?.items ?? []} contentType={contentType} canManage={canDraft} personId={personId} />
        <div className="delivery-pagination"><Button disabled={contentCursor === 0} onClick={() => setContentCursor(0)}>返回首页</Button>
          {contents.data?.nextCursor && <Button onClick={() => setContentCursor(contents.data!.nextCursor!)}>下一页</Button>}</div>
      </>}
      {selected.status==='COMPLETED'&&<div className="empty-dossier">该服务包已完成，业务内容保持只读；如需继续执行，请先撤销完成。</div>}
    </>;
  }

  function renderExecutorPackageDetail() {
    const activeItem = mine.data?.items.find((item) => item.packageId === activePackageId);
    return <>
      <header className="delivery-stage-banner"><div><span className="panel-number">CONTENT CONTEXT</span>
        <h2>{activeItem ? `${activeItem.packageTitle ?? activeItem.packageCode}` : '选择服务包'}</h2>
        <p>{COPY[contentType].title} · 任务和成果以真实后台状态为准</p></div>
        <div><small>本页内容</small><strong>{contents.data?.items.length ?? 0}</strong></div></header>
      {contents.isPending && <Skeleton active paragraph={{ rows: 6 }} />}
      {contents.isError && <Alert type="error" title="本人任务读取失败" description={failureMessage(contents.error)} />}
      {activePackageId && <PackageContentList projectId={projectId!} packageId={activePackageId} returnedContentId={returnedContentId}
        statusContext={searchParams.get('status')} qContext={searchParams.get('q')}
        contents={contents.data?.items ?? []} contentType={contentType} canManage={false} personId={personId} />}
      {contents.data?.nextCursor && <div className="delivery-pagination"><Button onClick={() => setContentCursor(contents.data!.nextCursor!)}>下一页内容</Button></div>}
    </>;
  }

  if (!projectId) return <main className="page-shell"><Alert type="error" title="项目地址不完整" /></main>;
  if (accessProfile.isPending) return <main className="page-shell"><Skeleton active paragraph={{ rows: 6 }} /></main>;
  if (accessProfile.isError || access.mode === 'NONE') return <main className="page-shell"><Alert type="error"
    title="当前项目暂无可用工作区" description={accessProfile.error ? failureMessage(accessProfile.error) :
      '当前项目分工不包含服务包管理、内容策划或内容执行。'} /></main>;
  if (pureExecutor) return <main className={`page-shell package-shell delivery-workbench${viewMode === 'table' ? ' is-table-mode' : ''}`}>
    <Link className="back-link" to="/projects">← 返回项目列表</Link>
    {viewMode === 'table' && <ModulePageHeader projectId={projectId} kicker={`项目数据 · ${project.data?.name ?? '项目'} · 服务包`}
      title="项目内容"
      copy={`${project.data?.name ?? COPY[contentType].title} · 查看项目全部内容（含已交付）；从整条内容进入，处理本人分工、修改与成果提交。`}
      stats={packageStats} actions={packageTypeSwitch} />}
    <div className="package-title delivery-page-title"><div><div className="section-kicker">PROJECT CONTENT / {project.data?.projectCode ?? '项目'}</div>
      <Title level={1}>项目内容</Title><p>{project.data?.name ?? COPY[contentType].title} · 查看项目全部内容（含已交付）；从整条内容进入，处理本人分工、修改与成果提交。</p></div>
      <div className="delivery-page-actions">
        {packageTypeSwitch}
        {viewMode === 'split' && <ModuleViewSwitch value={viewMode} onChange={changePackageView} />}</div></div>
    {project.isError && <Alert type="error" title="项目不可访问" description={failureMessage(project.error)} />}
    {viewMode === 'split' && <div className="delivery-layout-real">
      <aside className="delivery-package-rail">
        <header><div><span className="panel-number">PROJECT PACKAGES</span><h2>项目服务包</h2></div><b>{mine.data?.items.length ?? 0}</b></header>
        {mine.isPending && <Skeleton active paragraph={{ rows: 3 }} />}
        {mine.isError && <Alert type="error" title="项目服务包读取失败" description={failureMessage(mine.error)}
          action={<Button onClick={() => void mine.refetch()}>重试</Button>} />}
        {mine.data?.items.length === 0 && <div className="empty-dossier">当前项目暂无进行中或已完成的{COPY[contentType].title}。</div>}
        <div className="delivery-package-list">{mine.data?.items.map((item) => <button key={item.packageId}
          className={activePackageId === item.packageId ? 'is-active' : ''}
          onClick={() => { setSelectedPackageId(item.packageId); setContentCursor(0); }}>
          <small>{item.packageCode}</small><strong>{item.packageTitle ?? item.packageCode}</strong>
          <span>可只读查看项目全部内容与完整分工</span><i /></button>)}</div>
        <div className="delivery-privacy-note">可只读查看项目全部内容与完整分工；预算、他人金额与收款信息仍按本人权限隐藏。</div>
      </aside>
      <section className="delivery-stage-real">{renderExecutorPackageDetail()}</section>
    </div>}
    {viewMode === 'table' && mine.isError && <Alert type="error" title="项目服务包读取失败" description={failureMessage(mine.error)}
      action={<Button onClick={() => void mine.refetch()}>重试</Button>} />}
    {viewMode === 'table' && <ModuleTablePanel title={COPY[contentType].title} count={visiblePackageRows.length}
      filters={packageTableFilters} filterValue={tableFilter}
      onFilterChange={(value) => setTableFilter(value as PackageStatusFilter)}
      searchValue={tableSearch} onSearchChange={setTableSearch} searchPlaceholder={packageSearchPlaceholder}
      viewMode={viewMode} onViewChange={changePackageView}>
      <ModuleTableView rows={visiblePackageRows} columns={PACKAGE_TABLE_COLUMNS}
        rowKey={(row) => row.packageId} onOpen={openPackageDetail} focusedKey={tableDetail?.packageId ?? null}
        loading={mine.isPending} />
    </ModuleTablePanel>}
    <ModuleFullDetailDrawer open={viewMode === 'table' && !!tableDetail} onClose={closePackageDetail}>
      <section className="delivery-stage-real">{renderExecutorPackageDetail()}</section>
    </ModuleFullDetailDrawer>
  </main>;
  return <main className={`page-shell package-shell${viewMode === 'table' ? ' is-table-mode' : ''}`}>
    <Link className="back-link" to="/projects">← 返回我的项目</Link>
    {project.isPending && <Skeleton active paragraph={{ rows: 3 }} />}
    {project.isError && <Alert type="error" showIcon title="项目不可访问" description={failureMessage(project.error)} />}
    {project.data && <>
      {viewMode === 'table' && <ModulePageHeader projectId={projectId} kicker={`项目数据 · ${project.data.name} · 服务包`}
        title={contentType === 'VIDEO' ? '视频内容工作台' : '文章内容工作台'}
        copy={`${project.data.name} · ${project.data.businessEntity.name} · 负责人 ${project.data.primaryManager.displayName}`}
        stats={packageStats} actions={packageTypeSwitch} />}
      <div className="package-title delivery-page-title"><div><div className="section-kicker">{project.data.projectCode} / DELIVERY PANEL</div>
        <Title level={1}>{contentType === 'VIDEO' ? '视频内容工作台' : '文章内容工作台'}</Title><p>{project.data.name} · {project.data.businessEntity.name} · 负责人 {project.data.primaryManager.displayName}</p></div>
        <div className="delivery-page-actions">{viewMode === 'split' && <ModuleViewSwitch value={viewMode} onChange={changePackageView} />}
          {packageTypeSwitch}
          {canDraft && <Button onClick={() => setTeamOpen(true)}>项目团队</Button>}
          <Link to={`/projects/${projectId}/contracts`}><Button>合同档案</Button></Link></div></div>
        <div className="package-note">服务包管理容量、档位和费用边界；日常制作以每条视频或文章为单位。子任务只推进分工，整条内容提交后才进入审核与交付。</div>
      {error && <Alert className="workspace-alert" type="error" showIcon title={error} closable onClose={() => setError(null)} />}
      {success && <Alert className="workspace-alert" type="success" showIcon title={success} closable onClose={() => setSuccess(null)} />}
      {viewMode === 'split' && <div className="delivery-layout-real manager-delivery-layout">
        <aside className="delivery-package-rail">
          <header><div><span className="panel-number">SERVICE PACKAGES</span><h2>{COPY[contentType].title}</h2></div><b>{packages.data?.items.length ?? 0}</b></header>
          {canDraft && <Button block type="primary" onClick={() => {setEditingPackage(null);form.resetFields();
            form.setFieldsValue({businessOn:shanghaiToday()});attempt.current=null;setCreateOpen(true);}}><Icon name="plus" size={16} strokeWidth={2} /> 创建{contentType === 'VIDEO' ? '视频' : '文章'}服务包</Button>}
          {packages.isPending && <Skeleton active paragraph={{ rows: 4 }} />}
          {packages.isError && <Alert type="error" title="服务包读取失败" description={failureMessage(packages.error)}
            action={<Button onClick={() => void packages.refetch()}>重试</Button>} />}
          {packages.data?.items.length === 0 && <div className="empty-dossier">当前项目还没有{COPY[contentType].title}。</div>}
          <div className="delivery-package-list">{packages.data?.items.map((item) => {
            const used = item.tiers.reduce((sum, tier) => sum + tier.used, 0);
            return <button key={item.packageId} className={selectedPackage?.packageId === item.packageId ? 'is-active' : ''}
              onClick={() => { setSelectedPackageId(item.packageId); setContentCursor(0); }}>
              <small>{PACKAGE_STATUS_LABEL[item.status]} · {item.periodFrom} — {item.periodUntil}</small>
              <strong>{item.packageTitle ?? item.templateName}</strong>
              <span>{item.packageCode} · 已使用 {used}/{item.capacity}</span>
              <div className="delivery-mini-progress"><i style={{ width: `${Math.round(used / item.capacity * 100)}%` }} /></div>
            </button>;
          })}</div>
        </aside>
        <section className="delivery-stage-real">
          {selectedPackage ? renderManagerPackageDetail(selectedPackage) :
            <div className="delivery-stage-empty"><strong>请选择一个服务包</strong><span>在左侧选择后查看内容进度与任务。</span></div>}
        </section>
      </div>}
      {viewMode === 'table' && packages.isError && <Alert type="error" showIcon title="服务包读取失败"
        description={failureMessage(packages.error)} action={<Button onClick={() => void packages.refetch()}>重试</Button>} />}
      {viewMode === 'table' && <ModuleTablePanel title={COPY[contentType].title} count={visiblePackageRows.length}
        filters={packageTableFilters} filterValue={tableFilter}
        onFilterChange={(value) => setTableFilter(value as PackageStatusFilter)}
        searchValue={tableSearch} onSearchChange={setTableSearch} searchPlaceholder={packageSearchPlaceholder}
        viewMode={viewMode} onViewChange={changePackageView}
        action={canDraft && <Button type="primary" onClick={() => { setEditingPackage(null); form.resetFields();
          form.setFieldsValue({ businessOn: shanghaiToday() }); attempt.current = null; setCreateOpen(true); }}>
          <Icon name="plus" size={16} strokeWidth={2} /> 创建{contentType === 'VIDEO' ? '视频' : '文章'}服务包</Button>}>
        <ModuleTableView rows={visiblePackageRows} columns={PACKAGE_TABLE_COLUMNS}
          rowKey={(row) => row.packageId} onOpen={openPackageDetail} focusedKey={tableDetail?.packageId ?? null}
          loading={packages.isPending} />
      </ModuleTablePanel>}
      <ModuleFullDetailDrawer open={viewMode === 'table' && !!tableDetail} onClose={closePackageDetail}>
        <section className="delivery-stage-real">{tableManagerPackage ? renderManagerPackageDetail(tableManagerPackage) :
          <div className="delivery-stage-empty"><strong>请选择一个服务包</strong><span>在左侧选择后查看内容进度与任务。</span></div>}</section>
      </ModuleFullDetailDrawer>
      <Drawer title={`${editingPackage?'编辑':'创建'}${contentType === 'VIDEO' ? '视频' : '文章'}服务包草稿`} size={560} open={createOpen}
        onClose={() => {setCreateOpen(false);setEditingPackage(null);form.resetFields();}} destroyOnHidden>
        <p className="package-muted">选择统一服务模板并分配各规格数量。页面金额只用于预览，提交确认时仍由服务端重新核算。</p>
        <p className="package-muted">历史项目原模板族已归档：加包统一使用现行标准模板，新包的规格与费率按现行标准核算，可能与历史包不同。</p>
        <Form form={form} layout="vertical" initialValues={{ businessOn: shanghaiToday() }}
          onFinish={(values) => void create(values)}>
          <Form.Item name="packageTitle" label="服务包名称" rules={[{ max: 120, message: '服务包名称不能超过 120 个字' }]}>
            <Input placeholder="例如：袁治 2026 年视频服务（留空使用模板名称）" />
          </Form.Item>
          <Form.Item name="businessOn" label="业务日" rules={[{ required: true, message: '选择业务日' }]}>
            <DateInput onChange={() => { form.resetFields(['serviceTemplateVersionId','capacity','variants']); attempt.current = null; }} /></Form.Item>
          {options.isError && <Alert type="warning" showIcon title="可用标准读取失败" description={failureMessage(options.error)}
            action={<Button onClick={() => void options.refetch()}>重试</Button>} />}
          <Form.Item name="serviceTemplateVersionId" label="服务模板版本" rules={[{ required: true }]}>
            <Select loading={options.isFetching} showSearch optionFilterProp="label" placeholder="选择当日已生效模板"
              options={options.data?.templates.map((row) => ({ value: row.versionId, label: row.label })) ?? []}
              onChange={(id) => { const selected = options.data?.templates.find((row) => row.versionId === id);
                if (selected) form.setFieldsValue({ capacity: selected.defaultCapacity,
                  variants: selected.variants.map((variant) => ({ variantKey: variant.variantKey, count: 0 })) }); }} /></Form.Item>
          {template && <><div className="package-template-note"><strong>{template.label}</strong><span>{template.deliverable}</span></div>
            <Form.Item name="capacity" label={`${COPY[contentType].title}容量`} dependencies={['variants']}
              rules={[{ required: true },({getFieldValue})=>({validator(_,value){const total=(getFieldValue('variants')??[])
                .reduce((sum:number,row:{count?:number})=>sum+(row.count??0),0);return value===total?Promise.resolve():
                  Promise.reject(new Error(`各规格数量合计 ${total}，必须等于容量`));}})]}>
              <InputNumber min={1} max={100000} precision={0} /></Form.Item>
            <Form.List name="variants">{(fields) => <div className="package-tier-grid">{fields.map((field) => <div key={field.key}>
              <Form.Item name={[field.name, 'variantKey']} hidden><Input /></Form.Item><Form.Item name={[field.name, 'count']}
                label={`${template.variants[field.name]?.name ?? ''}（${template.variants[field.name]?.variantKey ?? ''}）数量`} rules={[{ required: true }]}>
                <InputNumber min={0} max={100000} precision={0} /></Form.Item></div>)}</div>}</Form.List>
            <div className="form-pair"><Form.Item name="periodFrom" label="执行开始日" rules={[{ required: true }]}><DateInput /></Form.Item>
              <Form.Item name="periodUntil" label="执行结束日" rules={[{ required: true }]}><DateInput /></Form.Item></div>
            <Form.Item name="flexibleReserve" label="灵活预留（元）" rules={[{ required: true },
              { pattern: /^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/, message: '填写非负金额，最多两位小数' }]}>
              <Input inputMode="decimal" placeholder="例如 1000 或 1000.50" /></Form.Item>
            <div className="package-budget-preview"><div><span>{contentType === 'VIDEO' ? '视频固定费用' : '内容固定费用'}</span><strong>¥{(estimatedProduction+estimatedPublication).toFixed(2)}</strong></div>
              <div><span>其中·发布任务</span><strong>¥{estimatedPublication.toFixed(2)}</strong></div>
              <div><span>灵活预留</span><strong>¥{Number(flexibleReserve||0).toFixed(2)}</strong></div>
              <div><span>预计总预算</span><strong>¥{(estimatedProduction+estimatedPublication+Number(flexibleReserve||0)).toFixed(2)}</strong></div></div>
            <div className="form-pair"><Form.Item name="managerId" label="服务包负责人" rules={[{required:true}]}>
              <Select showSearch optionFilterProp="label" options={options.data?.managers.map((row)=>({value:row.personId,label:row.label}))??[]}/></Form.Item>
              <Form.Item name="confirmerId" label="启动确认人" rules={[{required:true,message:'选择启动确认人'}]}>
                <Select showSearch optionFilterProp="label" options={options.data?.confirmers.map((row)=>({value:row.personId,label:row.label}))??[]}/></Form.Item></div>
            <Button block type="primary" htmlType="submit" loading={draftMutation.isPending || updateDraftMutation.isPending}>
              保存草稿</Button></>}
        </Form>
      </Drawer>
      <Drawer rootClassName="service-definition-drawer" size={820} open={definitionOpen}
        onClose={closeDefinitionEditor} destroyOnHidden
        title={<div className="definition-drawer-title"><small>ADMINISTRATOR ACTION</small>
          <strong>修改已创建服务包</strong>
          <span>{editingDefinitionPackage?.packageCode ?? '—'} · {editingDefinitionPackage
            ? PACKAGE_STATUS_LABEL[editingDefinitionPackage.status] : '读取中'}</span></div>}
        footer={editingDefinitionPackage && definitionTemplate ? <div className="definition-drawer-footer">
          <div className={error ? 'is-error' : undefined}><strong>{error ??
            `调整后 ${definitionPreview.capacity} 条 · ¥${definitionPreview.total.toFixed(2)}`}</strong>
            <span>{error ? '本次修改未保存，请调整后重试' : '保存后会写入修改记录'}</span></div>
          <Button onClick={closeDefinitionEditor}>取消</Button>
          <Button type="primary" onClick={() => definitionForm.submit()} loading={definitionMutation.isPending}
            disabled={!definitionCanSave}>保存服务包修改</Button>
        </div> : undefined}>
        <Alert className="definition-scope-note" type="info" showIcon title="本次只修改这个服务包的定义"
          description="以当前绑定的历史服务模板为基础人工确认。现有内容、交付、发布记录和已锁定任务金额不会删除或重建。" />
        {definitionOptions.isPending && <Skeleton active paragraph={{ rows: 8 }} />}
        {definitionOptions.isError && <Alert type="error" showIcon title="服务内容选项读取失败"
          description={failureMessage(definitionOptions.error)}
          action={<Button onClick={() => void definitionOptions.refetch()}>重试</Button>} />}
        {error && definitionOpen && <Alert className="definition-save-error" type="error" showIcon title={error} />}
        {editingDefinitionPackage && definitionTemplate && <Form className="definition-form" form={definitionForm} layout="vertical"
          onValuesChange={() => { if (error) setError(null); }}
          onFinishFailed={({ errorFields }) => {
            const first = errorFields[0];
            const message = first?.errors[0]?.toString() ?? '请检查未完整或不符合要求的字段。';
            setError(message); notifyActionFailure(message);
            if (first) definitionForm.scrollToField(first.name);
          }}
          onFinish={(values) => void saveDefinition(values)}>
          <section className="definition-section">
            <header className="definition-section-title"><b>01</b><div><strong>基础信息</strong>
              <span>调整本包的名称、日期和项目关系人。</span></div></header>
            <Form.Item name="packageTitle" label="服务包名称" rules={[{ max: 120 }]}>
              <Input placeholder={editingDefinitionPackage.templateName} /></Form.Item>
            <div className="definition-field-grid definition-field-grid-three">
              <Form.Item name="businessOn" label="业务日" rules={[{ required: true }]}><DateInput /></Form.Item>
              <Form.Item name="periodFrom" label="执行开始日" rules={[{ required: true }]}><DateInput /></Form.Item>
              <Form.Item name="periodUntil" label="执行结束日" rules={[{ required: true }]}><DateInput /></Form.Item>
            </div>
            <div className="definition-field-grid">
              <Form.Item name="managerId" label="服务包负责人" rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={definitionOptions.data?.managers.map((row) =>
                  ({ value: row.personId, label: row.label })) ?? []} /></Form.Item>
              <Form.Item name="confirmerId" label="启动确认人" rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={definitionOptions.data?.confirmers.map((row) =>
                  ({ value: row.personId, label: row.label })) ?? []} /></Form.Item>
            </div>
          </section>

          <section className="definition-section definition-service-section">
            <header className="definition-section-title"><b>02</b><div><strong>服务内容与条数</strong>
              <span>{definitionTemplate.label} · {definitionTemplate.variants.length} 个费率档位</span></div><em>历史模板</em></header>
            <div className="definition-service-guide"><span><i />当前条数</span><span><i />已录入下限</span>
              <small>新条数不能小于已录入数</small></div>
            <Form.List name="variants" initialValue={normalizePackageDefinitionVariants(definitionTemplate.variants,
              editingDefinitionPackage.tiers.map((tier) => ({ variantKey: tier.code, count: tier.count })))}>
              {(fields) => <div className="definition-service-list">{fields.map((field) => {
            const variant = definitionTemplate.variants[field.name];
            const group = definitionGroupMeta(definitionTemplate.variants, field.name);
            const currentTier = editingDefinitionPackage.tiers.find((tier) => tier.code === variant?.variantKey);
            const used = currentTier?.used ?? 0;
            const currentCount = definitionVariantCounts[field.name]?.count ?? 0;
            const invalid = currentCount < used;
            return <Fragment key={field.key}>
              {group && <div className="definition-service-group"><strong>{group.name}</strong><small>{group.count} 个费率档位</small></div>}
              <div className={`definition-service-row${invalid ? ' is-invalid' : ''}`}>
              <Form.Item name={[field.name, 'variantKey']} hidden><Input /></Form.Item>
              <div className="definition-service-copy"><strong>{variant?.name ?? '服务内容'}</strong>
                <small>{variant?.variantKey ?? '—'} · {variant?.rateLabel ?? variant?.description ?? '历史模板服务规格'}</small></div>
              <div className="definition-service-rate"><strong>¥{variant?.totalBudget ?? '0.00'} / 条</strong>
                <small>制作 ¥{variant?.productionBudget ?? '0.00'} · 发布 ¥{variant?.publicationBudget ?? '0.00'}</small></div>
              <div className="definition-counter">
                <Button type="text" aria-label={`减少${variant?.name ?? '服务内容'}条数`}
                  onClick={() => changeDefinitionCount(field.name, -1, used)}>−</Button>
                <Form.Item name={[field.name, 'count']} rules={[{ validator: (_, value) => {
                  const count = typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
                  return count >= used ? Promise.resolve()
                    : Promise.reject(new Error(`不能低于已录入的 ${used} 条`));
                } }]}>
                  <InputNumber min={0} max={100000} precision={0} controls={false} aria-label={`${variant?.name ?? '服务内容'}条数`} />
                </Form.Item>
                <Button type="text" aria-label={`增加${variant?.name ?? '服务内容'}条数`}
                  onClick={() => changeDefinitionCount(field.name, 1, used)}>+</Button>
              </div>
              <small className="definition-service-floor">已录入 <b>{used}</b> 条 · 原定 {currentTier?.count ?? 0} 条</small>
              </div>
            </Fragment>;
          })}</div>}</Form.List>
          </section>

          <section className="definition-section definition-budget-section">
            <header className="definition-section-title"><b>03</b><div><strong>预算确认</strong>
              <span>调整条数后由系统按历史模板单价重新计算。</span></div></header>
            <div className="definition-budget-grid"><div><span>调整后总条数</span><strong>{definitionPreview.capacity}</strong>
              <small>已录入 {definitionUsed} 条</small></div>
              <div><span>制作预算</span><strong>¥{definitionPreview.production.toFixed(2)}</strong><small>策划、拍摄、剪辑等</small></div>
              <div><span>发布预算</span><strong>¥{definitionPreview.publication.toFixed(2)}</strong><small>按历史模板计算</small></div>
              <div className="definition-budget-total"><span>预计总预算</span><strong>¥{definitionPreview.total.toFixed(2)}</strong>
                <small>{definitionBudgetDelta === 0 ? '与修改前一致' :
                  `${definitionBudgetDelta > 0 ? '增加' : '减少'} ¥${Math.abs(definitionBudgetDelta).toFixed(2)}`}</small></div></div>
            <Form.Item className="definition-reserve" name="flexibleReserve" label="灵活预留（元）" extra="用于未被固定服务规格覆盖的已确认工作。"
              rules={[{ required: true }, { pattern: /^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/,
                message: '填写非负金额，最多两位小数' }]}><Input inputMode="decimal" /></Form.Item>
          </section>

          <section className="definition-section definition-reason-section">
            <header className="definition-section-title"><b>04</b><div><strong>修改说明</strong>
              <span>说明本次人工确认的依据，保留在服务包审计记录中。</span></div></header>
            <Form.Item name="reason" label={<span>修改原因 <b className="definition-required">必填</b></span>}
              extra={<span className="definition-reason-count">{definitionReason.length} / 500</span>}
              rules={[{ required: true, min: 2, max: 500, message: '请填写 2–500 字修改原因' }]}>
              <Input.TextArea rows={3} maxLength={500} placeholder="例如：根据客户合同与当前实际服务内容人工确认" /></Form.Item>
          </section>
        </Form>}
      </Drawer>
      <Modal title={actionDialog==='return'?'退回服务包':actionDialog==='reopen'?'撤销完成':'完成服务包'}
        open={!!actionDialog} onCancel={()=>{setActionDialog(null);actionForm.resetFields();}}
        onOk={()=>actionForm.submit()} confirmLoading={false} okText="确认">
        <Form form={actionForm} layout="vertical" onFinish={(values)=>void submitAction(values)}>
          {actionDialog==='complete'?<><Form.Item name="completedOn" label="真实完成日期" rules={[{required:true}]}><DateInput /></Form.Item>
            <Form.Item name="note" label="完成说明" rules={[{required:true,min:2,max:1000}]}><Input.TextArea rows={4}/></Form.Item></>:
            <Form.Item name="reason" label={actionDialog==='return'?'退回原因':'撤销完成原因'} rules={[{required:true,min:2,max:500}]}>
              <Input.TextArea rows={4}/></Form.Item>}
        </Form>
      </Modal>
      {selectedPackage?.status === 'ACTIVE' && <ContentCreateDrawer open={contentCreateOpen}
        onClose={() => setContentCreateOpen(false)} token={token} accountId={accountId} projectId={projectId}
        packageId={selectedPackage.packageId} contentType={contentType} variants={selectedPackage.tiers}
        executorOptions={executors.data?.items} taskOptions={executors.data?.tasks} />}
      <ProjectTeamDrawer open={teamOpen} onClose={() => setTeamOpen(false)} token={token}
        accountId={accountId} projectId={projectId} />
    </>}
  </main>;
}
